import { createElement, useState, useCallback, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StatusIndicator } from "@/components/ui/status-indicator";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertCircle,
  ArrowLeft,
  Banknote,
  CreditCard,
  Loader2,
  MoreHorizontal,
  Pencil,
  Receipt,
  Settings,
  Settings2,
  AlertTriangle,
  Calendar,
} from "lucide-react";
import {
  EditResourceBudgetPage,
} from "@/components/blocks/budget/edit-resource-budget-page";
import { EditCostBudgetPage } from "@/components/blocks/budget/edit-cost-budget-page";
import { EditSalesBudgetPage } from "@/components/blocks/budget/edit-sales-budget-page";
import { EditSalesBudgetPageFP } from "@/components/blocks/budget/edit-sales-budget-page-fp";
import {
  EditBudgetModal,
  BudgetExportModal,
} from "@/components/blocks/budget";
import { ResourceRequestModal } from "@/components/resource-request-modal";
import { TabSettingsDialog } from "@/components/tab-settings-dialog";
import { ConfigureStructureModal } from "@/components/blocks/cost-centres/configure-structure-modal";
import { CostCentreDetailsPanel } from "@/components/blocks/cost-centres/cost-centre-details-panel";
import { CostCentreFullModal } from "@/components/blocks/cost-centres/cost-centre-full-modal";
import { CreateCostCentrePanel } from "@/components/blocks/cost-centres/create-cost-centre-panel";

import type { CostCentre } from "@/components/blocks/cost-centres/cost-centres-table";
import { BUDGET_ONLY_COLUMN_KEYS } from "./tabs/cost-centres-tab";
import {
  saveColumnPreferences,
  getDataMode,
  createCostCentre,
  fetchCostCentreStructureByProjectId,
  saveCostCentreStructure,
  updateCostCentre,
} from "@/lib/data-service";
import { InvoiceGenerationWizard } from "@/components/billing/InvoiceGenerationWizard";
import { InvoiceDetailsSheet } from "@/components/billing/InvoiceDetailsSheet";
import { CreateDraftDialog } from "@/components/billing/CreateDraftDialog";
import { ClientSidePanel } from '@/components/client-side-panel';
import { ClientChangeConfirmDialog } from "@/components/client-change-confirm-dialog";
import { ColumnConfiguratorDrawer, type ColumnDefinition } from "@/components/column-configurator-drawer";
import { ROUTES } from "@/routes";
import { PROJECT_STATUS_LABELS } from "@/types";
import type { ProjectDetailsPageProps } from "./types";
import type { InvoicePdfTransaction } from "@/types/invoice-pdf";
import { DEFAULT_INVOICE_COLUMNS, DEFAULT_WIP_COLUMNS } from "./types";
import {
  createBillingDraft,
  updateBillingDraft,
  updateDraftEmailData,
  postBillingDraftToFinancials,
  fetchBillingInvoiceById,
} from "@/lib/data-service";
import { updateProjectBillingConfig } from "@/services/billingService";
import { DEFAULT_EMAIL_TEMPLATES } from "@/components/billing/EmailTemplateEditor";
import type { EmailTemplate, EmailTemplateType } from "@/components/billing/EmailTemplateEditor";
import { fetchEmailTemplates } from '@/services/emailTemplateService';
import type { ApiEmailTemplate } from '@/services/emailTemplateService';
import { generateAndStoreInvoicePdf, fetchFullCompanyDetails, blobToBase64 } from "@/services/invoice-pdf-service";
import { uploadInvoicePdfToAdm, storeAdmDocumentId } from "@/services/adm-upload-service";
import { useDocumentApi } from "@/providers/DocumentApiProvider";
import { toast } from "sonner";
import {
  healthToVariant,
  cn,
  toLocalDateString,
} from "@/lib/utils";
import { getProjectStatusColor } from "@/lib/status-colors";
import { usePermissions } from '@/contexts/PermissionsContext';
import { saveRevision, activateBudgetRevision } from '@/lib/data-service';
import { getBudgetHealthThresholds, deriveProjectHealthWithBreakdown } from '@/lib/budget-health-thresholds-storage';
import { CascadeTargetResolutionDialog } from '@/components/blocks/budget/cascade-target-resolution-dialog';

// Refactored modules
import { ProjectDetailsContext } from "./context";
import { useProjectDetails } from "./hooks/use-project-details";
import { OverviewTab } from "./tabs/overview-tab";
import { FinancialsTab } from "./tabs/financials-tab";
import { BillingTab } from "./tabs/billing-tab";
import { SalesBudgetTab } from "./tabs/sales-budget-tab";
import { CostBudgetTab } from "./tabs/cost-budget-tab";
import { ResourceBudgetTab } from "./tabs/resource-budget-tab";
import { CostCentresTab } from "./tabs/cost-centres-tab";
import { PurchasesTab } from "./tabs/purchases-tab";
import { TeamTab } from "./tabs/team-tab";
import { ResourcingTab } from "./tabs/resourcing-tab";
import { TimeTab } from "./tabs/time-tab";
import { ExpensesTab } from "./tabs/expenses-tab";
import { AuditTab } from "./tabs/audit-tab";
import { ProjectReportsTab } from "@/components/ProjectReportsTab";
import type { EditDraftData } from "@/components/billing/InvoiceGenerationWizard";

/**
 * Edit-Draft mode — apply the wizard's edited/removed lines to an existing draft.
 *
 * Diffs the wizard's current detail-line snapshot (`draftLineState`) against the
 * draft's original line items to derive per-WIP edits and removals, then persists
 * them via a single updateBillingDraft call. No-op when nothing changed.
 */
async function applyDraftLineChanges(
  editingDraftId: number,
  originalLines: EditDraftData["lineItems"],
  draftLineState: Array<{ wipLedgerId?: number; quantity: number; rate: number; netAmount: number; description: string }>,
): Promise<void> {
  const originalByWip = new Map<number, { quantity: number; rate: number; netAmount: number; description: string }>();
  for (const li of originalLines) {
    if (li.wipLedgerId != null) {
      originalByWip.set(li.wipLedgerId, { quantity: li.quantity, rate: li.rate, netAmount: li.netAmount, description: li.description });
    }
  }

  const remainingWipIds = new Set<number>();
  const edits: Array<{ wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string }> = [];
  for (const line of draftLineState) {
    if (line.wipLedgerId == null) continue;
    remainingWipIds.add(line.wipLedgerId);
    const orig = originalByWip.get(line.wipLedgerId);
    if (!orig) continue;
    const edit: { wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string } = { wipLedgerId: line.wipLedgerId };
    let changed = false;
    if (line.quantity !== orig.quantity) { edit.quantity = line.quantity; changed = true; }
    if (line.rate !== orig.rate) { edit.rate = line.rate; changed = true; }
    if (line.netAmount !== orig.netAmount) { edit.netAmount = line.netAmount; changed = true; }
    if (line.description !== orig.description) { edit.description = line.description; changed = true; }
    if (changed) edits.push(edit);
  }

  const removals: number[] = [];
  originalByWip.forEach((_value, wipId) => {
    if (!remainingWipIds.has(wipId)) removals.push(wipId);
  });

  if (removals.length > 0 || edits.length > 0) {
    await updateBillingDraft(editingDraftId, { removals, edits });
  }
}

export function ProjectDetailsPage({ projectId: projectIdProp, onBack }: ProjectDetailsPageProps) {
  const ctx = useProjectDetails({ projectId: projectIdProp, onBack });
  const { canPerformAction } = usePermissions();

  const {
    project, client, clients, accountManager, projectManager,
    projectId, numericProjectId, navigate, activeTab, setActiveTab,
    budgetData, refetchBudget, enrichedBudgetData,
    setLocalBudgetData, budgetPermissions,
    updateBudget,
    raiseRequestModalOpen, setRaiseRequestModalOpen,
    raiseRequestPrefilledRole, raiseRequestCostCentre,
    handleRequestSubmitSuccess, liveRoleCoverage,
    billingActionLoading, setBillingActionLoading,
    billingInvoices, setBillingInvoices,
    billingSubTab, setBillingSubTab,
    refetchBillingData, handleCreateDraft, handleDeleteDraft,
    wipItems,
    selectedWipItems, setSelectedWipItems,
    invoiceColumns, updateInvoiceColumns,
    invoiceColumnConfigOpen, setInvoiceColumnConfigOpen,
    wipColumns, updateWipColumns, wipColumnConfigOpen, setWipColumnConfigOpen,
    invoiceWizardOpen, setInvoiceWizardOpen,
    editDraftData, setEditDraftData,
    fixedFeeMilestone, setFixedFeeMilestone,
    invoiceDetailsSheetOpen, setInvoiceDetailsSheetOpen,
    selectedInvoiceId,
    createDraftDialogOpen, setCreateDraftDialogOpen,
    projectSettings, setProjectSettings,
    billingSettingsDrawerOpen,
    drawerEditMode, drawerSettings, drawerSaving,
    salesBudgetEditModalOpen, setSalesBudgetEditModalOpen,
    costBudgetEditModalOpen, setCostBudgetEditModalOpen,
    resourceBudgetEditModalOpen, setResourceBudgetEditModalOpen,
    editSalesBudgetPageOpen, setEditSalesBudgetPageOpen,
    editCostBudgetPageOpen, setEditCostBudgetPageOpen,
    editResourceBudgetPageOpen, setEditResourceBudgetPageOpen,
    budgetExportModalOpen, setBudgetExportModalOpen,
    setRevisionHistoryOpen,
    setShowCostRevisionHistory,
    costLoadedRevision, setCostLoadedRevision,
    costMakeActiveRevision, setCostMakeActiveRevision,
    costMakeActiveSource, setCostMakeActiveSource,
    isCostNewRevision, setIsCostNewRevision,
    setShowSalesRevisionHistory,
    salesLoadedRevision, setSalesLoadedRevision,
    salesMakeActiveRevision, setSalesMakeActiveRevision,
    salesMakeActiveSource, setSalesMakeActiveSource,
    isSalesNewRevision, setIsSalesNewRevision,
    viewingRevision, setViewingRevision,
    viewingRevisionPageOpen, setViewingRevisionPageOpen,
    newRevisionPageOpen, setNewRevisionPageOpen,
    duplicateRevision, setDuplicateRevision,
    makeActiveRevision, setMakeActiveRevision,
    makeActiveSource, setMakeActiveSource,
    costCentres, setCostCentres,
    hierarchicalCostCentres, setHierarchicalCostCentres,
    isStructureView,
    costCentreColumns, setCostCentreColumns,
    costCentreColumnConfigOpen, setCostCentreColumnConfigOpen,
    selectedCostCentre, setSelectedCostCentre,
    costCentreDetailsPanelOpen, setCostCentreDetailsPanelOpen,
    costCentreFullModalOpen, setCostCentreFullModalOpen,
    createCostCentrePanelOpen, setCreateCostCentrePanelOpen,
    configureStructureOpen, setConfigureStructureOpen,
    defaultCostCentreError, setDefaultCostCentreError,
    handleCompletionModeChange,
    isNameEditing, isDescEditing,
    inlineNameValue, setInlineNameValue,
    inlineDescValue, setInlineDescValue,
    showCancelDialog, setShowCancelDialog,
    handleConfirmCancelUnified,
    clientChangeDialogOpen,
    pendingClientChange, previousClientForDialog,
    showPmConfirmDialog, setShowPmConfirmDialog,
    pendingPmChange,
    showAmConfirmDialog, setShowAmConfirmDialog,
    pendingAmChange,
    projectNameHasIssuedInvoices,
    handleSaveBudget,
    handleDrawerOpen, handleDrawerEdit, handleDrawerCancel, handleDrawerSave,
    handleDrawerSettingsChange,
    handleInlineStatusChange,
    handleNameEditStart, handleNameSave, handleNameKeyDown,
    handleDescEditStart, handleDescSave, handleDescKeyDown,
    handleClientChangeConfirm, handleClientChangeCancel,
    handleConfirmPmChange, handleCancelPmChange,
    handleConfirmAmChange, handleCancelAmChange,
    getNextInvoiceDateForDrawer,
    getDefaultBudgetData,
    dataMode,
    currentCompany, invoiceTemplates,
    selectableStatuses,
    activePanelClientId, closeClientPanel,
    handleApiError,
    selectedTabIds,
    tabSettingsOpen, setTabSettingsOpen, tabConfigsForDialog,
    visibleTabs, moreTabs, pinnedMoreTab, setPinnedMoreTab,
    saveTabOrder, tabsContainerCallbackRef,
    isStatusSaving, loading,
    createBudgetRevision,
    realisationRatePct,
  } = ctx;

  // ── ADM document upload context
  const { getDocumentContext, getAccessToken: getAdmAccessToken } = useDocumentApi();

  // ── Email templates — lazy-loaded when the billing settings drawer opens
  const [allEmailTemplates, setAllEmailTemplates] = useState<EmailTemplate[]>(DEFAULT_EMAIL_TEMPLATES);
  const emailTemplatesLoadedRef = useRef(false);

  useEffect(() => {
    if (!billingSettingsDrawerOpen) return;
    if (emailTemplatesLoadedRef.current) return;
    if (dataMode !== 'live') {
      setAllEmailTemplates(DEFAULT_EMAIL_TEMPLATES);
      return;
    }
    emailTemplatesLoadedRef.current = true;
    let cancelled = false;
    fetchEmailTemplates()
      .then((apiTemplates) => {
        if (!cancelled) {
          setAllEmailTemplates(apiTemplates.map((api: ApiEmailTemplate): EmailTemplate => {
            let parsedVars: { to?: string[]; cc?: string[]; bcc?: string[] } = {};
            if (api.variables) {
              try { parsedVars = JSON.parse(api.variables); } catch { /* ignore */ }
            }
            return {
              id: String(api.id),
              name: api.name,
              type: (api.category || 'invoice') as EmailTemplateType,
              to: parsedVars.to ?? [],
              cc: parsedVars.cc ?? [],
              bcc: parsedVars.bcc ?? [],
              subject: api.subject,
              body: '',
              bodyHtml: api.body,
              isDefault: api.isDefault,
              createdAt: new Date(api.createdAt),
              updatedAt: new Date(api.updatedAt),
            };
          }));
        }
      })
      .catch((err) => {
        console.error('Failed to load email templates:', err);
        if (!cancelled) {
          setAllEmailTemplates(DEFAULT_EMAIL_TEMPLATES);
        }
      });
    return () => { cancelled = true; };
  }, [billingSettingsDrawerOpen, dataMode]);

  const invoiceEmailTemplates: EmailTemplate[] = allEmailTemplates.filter((t) => t.type === 'invoice');
  const creditNoteEmailTemplates: EmailTemplate[] = allEmailTemplates.filter((t) => t.type === 'credit_note');

  // Refetch cost centres from API after mutations to ensure consistency
  const refetchCostCentres = async () => {
    if (getDataMode() !== 'live' || !numericProjectId) return;
    try {
      const freshCostCentres = await fetchCostCentreStructureByProjectId(numericProjectId);
      setCostCentres(freshCostCentres.filter(cc => !cc.isStructureNode));
      setHierarchicalCostCentres(freshCostCentres);
    } catch (error) {
      console.error('Failed to refetch cost centres:', error);
    }
  };

  // ── Health badge/tooltip.
  // The badge value comes from the server-derived project.health (usp_Project_GetById
  // computes the same multi-dimensional rollup, including realisation rate), so the
  // detail page matches the list exactly and no longer flashes before timesheet data
  // loads. The per-dimension breakdown below is the tooltip detail; it is fed the
  // server realisation rate (falling back to the locally computed one) so it agrees
  // with the badge.
  const effectiveRealisationRatePct = project?.realisationRatePct ?? realisationRatePct;
  const enrichedHealthBreakdown = (() => {
    if (!project) return null;
    const thresholds = getBudgetHealthThresholds();
    return deriveProjectHealthWithBreakdown(
      {
        actualCost: (project.actualLabour ?? 0) + (project.actualExpense ?? 0),
        budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
        startDate: project.startDate,
        endDate: project.endDate,
        completionPercent: project.completionPercentOverride ?? project.completionPercent ?? 0,
        actualHours: project.actualHours ?? 0,
        budgetHours: project.budgetHours ?? 0,
        wipAmount: project.wipAmount ?? 0,
        actualRevenue: project.actualRevenue ?? 0,
        budgetRevenue: project.budgetRevenue ?? 0,
        actualGrossMarginPct: null, // Gross margin handled separately via budget data
        realisationRatePct: effectiveRealisationRatePct,
        forecastEndDate: project.forecastEndDate,
      },
      thresholds
    );
  })();
  const enrichedProjectHealth = project?.health ?? null;

  // Cascade target resolution dialog state for "Make Active" flow
  const [showMakeActiveCascadeTarget, setShowMakeActiveCascadeTarget] = useState(false);
  const [pendingMakeActiveRevision, setPendingMakeActiveRevision] = useState<import('@/types/budget.types').BudgetRevision | null>(null);

  const executeMakeActiveCascade = useCallback((resolvedTargetId: number | null) => {
    const revision = pendingMakeActiveRevision ?? makeActiveRevision;
    if (!revision || !enrichedBudgetData) return;

    const updatedRevisions = (enrichedBudgetData.revisions ?? []).map((r) => {
      const isTarget = r.id === revision.id;
      if (isTarget) return { ...r, status: 'current' as const };
      if (r.status === 'current' || r.status === 'active') return { ...r, status: 'historical' as const };
      return r;
    });

    const snapshot = revision.resourceSnapshot ?? [];
    const updatedByRole: import('@/types/budget.types').RoleBudget[] = snapshot.map((snap, idx) => ({
      id: snap.roleId ?? idx,
      role: snap.role,
      budgetHours: snap.budgetHours,
      allocatedHours: snap.budgetHours,
      actualHours: 0,
      remainingHours: snap.budgetHours,
      percentUsed: 0,
      billRate: snap.billRate,
      costRate: snap.costRate,
      budgetCost: snap.budgetCost,
      actualCost: 0,
    }));

    const revisionSettings = budgetPermissions.settings.revisionSettings;
    const shouldCascadeCost = revisionSettings?.autoUpdateCostLabour ?? false;
    const shouldCascadeSales = revisionSettings?.autoUpdateSalesLabour ?? false;
    const cascadeTargetId = resolvedTargetId ?? revisionSettings?.cascadeTargetCategoryId ?? null;

    const newCostLabour = snapshot.reduce((sum, snap) => sum + (snap.budgetCost ?? 0), 0);
    const newSalesLabour = snapshot.reduce((sum, snap) => sum + (snap.budgetHours * snap.billRate), 0);

    const isCascadeTarget = (cat: { id: number; type: string }) =>
      cascadeTargetId != null ? cat.id === cascadeTargetId : cat.type === 'labour';

    const buildCategorySnapshot = (categories: Array<{ id: number; name: string; type: import('@/types/budget.types').CostCategoryType | import('@/types/budget.types').SalesCategoryType; budget: number }>): import('@/types/budget.types').RevisionCategorySnapshot[] =>
      categories.map(cat => ({
        categoryId: cat.id,
        categoryName: cat.name,
        type: cat.type as import('@/types/budget.types').CostCategoryType,
        budget: cat.budget,
      }));

    const nextRevNum = (layer: 'cost' | 'sales') =>
      Math.max(0, ...(enrichedBudgetData.revisions ?? [])
        .filter(r => (r.budgetLayer ?? 'resource') === layer)
        .map(r => r.revisionNumber ?? 0)) + 1;

    let updatedCostBudget = enrichedBudgetData.costBudget;
    let updatedSalesBudget = enrichedBudgetData.salesBudget;
    const cascadeRevisions: import('@/types/budget.types').BudgetRevision[] = [];

    if (shouldCascadeCost && enrichedBudgetData.costBudget) {
      const prevLabour = enrichedBudgetData.costBudget.categories.filter(c => isCascadeTarget(c)).reduce((sum, c) => sum + c.budget, 0);
      const updatedCostCategories = enrichedBudgetData.costBudget.categories.map(cat => isCascadeTarget(cat) ? { ...cat, budget: newCostLabour } : cat);
      const newCostTotal = updatedCostCategories.reduce((sum, c) => sum + c.budget, 0);
      updatedCostBudget = { ...enrichedBudgetData.costBudget, categories: updatedCostCategories, totals: { ...enrichedBudgetData.costBudget.totals, budget: newCostTotal } };
      const prevActiveCostRevId = (enrichedBudgetData.revisions ?? []).find(r => (r.budgetLayer ?? 'resource') === 'cost' && r.status === 'active')?.id;
      if (prevActiveCostRevId != null) { const idx = updatedRevisions.findIndex(r => r.id === prevActiveCostRevId); if (idx !== -1) updatedRevisions[idx] = { ...updatedRevisions[idx], status: 'historical' as const }; }
      const cbRevNum = nextRevNum('cost');
      cascadeRevisions.push({ id: Date.now() + 1, projectId: numericProjectId, revisionNumber: cbRevNum, date: new Date().toISOString(), budgetLayer: 'cost', status: 'active', reasonCategory: 'resource-budget-cascade', reasonCategoryLabel: 'Resource Budget Cascade', reason: `Labour updated via Resource Budget revision RB-v${revision.revisionNumber}`, createdAt: new Date().toISOString(), changedBy: 'System', changedByName: 'System', costBudget: newCostTotal, salesBudget: 0, resourceBudget: 0, cascadeSource: true, linkedResourceRevisionId: revision.id, totalBudgetAtRevision: newCostTotal, budgetChange: newCostLabour - prevLabour, costSnapshot: buildCategorySnapshot(updatedCostCategories), categoryChanges: enrichedBudgetData.costBudget.categories.filter(c => isCascadeTarget(c)).map(c => ({ categoryId: c.id, categoryName: c.name, type: c.type, previousBudget: c.budget, newBudget: newCostLabour, budgetDelta: newCostLabour - c.budget, percentChange: c.budget > 0 ? ((newCostLabour - c.budget) / c.budget) * 100 : 0, changeType: (newCostLabour > c.budget ? 'increased' : newCostLabour < c.budget ? 'decreased' : 'unchanged') as 'increased' | 'decreased' | 'unchanged' })) });
      toast.success(`Cost Budget Labour updated — CB-v${cbRevNum} created automatically from RB-v${revision.revisionNumber}`);
    }

    if (shouldCascadeSales && enrichedBudgetData.salesBudget) {
      const salesCats = enrichedBudgetData.salesBudget.categories ?? [];
      const prevSalesLabour = salesCats.filter(c => isCascadeTarget(c)).reduce((sum, c) => sum + c.budget, 0);
      const updatedSalesCategories = salesCats.map(cat => isCascadeTarget(cat) ? { ...cat, budget: newSalesLabour } : cat);
      const newSalesTotal = updatedSalesCategories.reduce((sum, c) => sum + c.budget, 0);
      updatedSalesBudget = { ...enrichedBudgetData.salesBudget, categories: updatedSalesCategories };
      const prevActiveSalesRevId = (enrichedBudgetData.revisions ?? []).find(r => (r.budgetLayer ?? 'resource') === 'sales' && r.status === 'active')?.id;
      if (prevActiveSalesRevId != null) { const idx = updatedRevisions.findIndex(r => r.id === prevActiveSalesRevId); if (idx !== -1) updatedRevisions[idx] = { ...updatedRevisions[idx], status: 'historical' as const }; }
      const sbRevNum = nextRevNum('sales');
      cascadeRevisions.push({ id: Date.now() + 2, projectId: numericProjectId, revisionNumber: sbRevNum, date: new Date().toISOString(), budgetLayer: 'sales', status: 'active', reasonCategory: 'resource-budget-cascade', reasonCategoryLabel: 'Resource Budget Cascade', reason: `Labour updated via Resource Budget revision RB-v${revision.revisionNumber}`, createdAt: new Date().toISOString(), changedBy: 'System', changedByName: 'System', costBudget: 0, salesBudget: newSalesTotal, resourceBudget: 0, cascadeSource: true, linkedResourceRevisionId: revision.id, totalBudgetAtRevision: newSalesTotal, budgetChange: newSalesLabour - prevSalesLabour, salesSnapshot: buildCategorySnapshot(updatedSalesCategories), categoryChanges: salesCats.filter(c => isCascadeTarget(c)).map(c => ({ categoryId: c.id, categoryName: c.name, type: c.type, previousBudget: c.budget, newBudget: newSalesLabour, budgetDelta: newSalesLabour - c.budget, percentChange: c.budget > 0 ? ((newSalesLabour - c.budget) / c.budget) * 100 : 0, changeType: (newSalesLabour > c.budget ? 'increased' : newSalesLabour < c.budget ? 'decreased' : 'unchanged') as 'increased' | 'decreased' | 'unchanged' })) });
      toast.success(`Sales Budget Labour updated — SB-v${sbRevNum} created automatically from RB-v${revision.revisionNumber}`);
    }

    setLocalBudgetData({
      ...enrichedBudgetData,
      revisions: [...updatedRevisions, ...cascadeRevisions],
      resourceBudget: enrichedBudgetData.resourceBudget
        ? { ...enrichedBudgetData.resourceBudget, byRole: updatedByRole.length > 0 ? updatedByRole : enrichedBudgetData.resourceBudget.byRole }
        : enrichedBudgetData.resourceBudget,
      costBudget: updatedCostBudget,
      salesBudget: updatedSalesBudget,
    });

    toast.success(makeActiveSource === 'restore'
      ? `RB-v${revision.revisionNumber} has been restored as the active revision.`
      : `RB-v${revision.revisionNumber} is now the active revision.`
    );
    setRevisionHistoryOpen(true);

    setMakeActiveRevision(null);
    setPendingMakeActiveRevision(null);
    setViewingRevisionPageOpen(false);
    setViewingRevision(null);
  }, [pendingMakeActiveRevision, makeActiveRevision, enrichedBudgetData, budgetPermissions, numericProjectId, makeActiveSource, setLocalBudgetData, setRevisionHistoryOpen, setMakeActiveRevision, setViewingRevisionPageOpen, setViewingRevision]);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Skeleton for Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-full mb-1" />
                <Skeleton className="h-3 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Skeleton for Tabs */}
        <Card>
          <CardHeader>
            <div className="flex space-x-2">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
            <div className="pt-4">
              <Skeleton className="h-64 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div className="text-muted-foreground">Project not found</div>
        <Button onClick={() => (onBack ? onBack() : navigate(ROUTES.PROJECTS))}>Back to Projects</Button>
      </div>
    );
  }

  // Show full-screen edit page if editing cost budget
  if (editCostBudgetPageOpen) {
    const budgetDataToUse = budgetData || getDefaultBudgetData();
    // Override projectName with actual project name/code instead of the placeholder from the API
    const projectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || budgetDataToUse.projectName;
    const budgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: projectDisplayName,
    };
    return (
      <EditCostBudgetPage
        budgetData={budgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        loadedFromRevision={costLoadedRevision ?? undefined}
        isNewRevision={isCostNewRevision}
        resourceBudget={budgetDataToUse.resourceBudget}
        requireRevisionNotes={budgetPermissions.settings.revisionSettings?.requireRevisionNotesOnCostSave}
        cascadeTargetCategoryId={budgetPermissions.settings.revisionSettings?.cascadeTargetCategoryId ?? null}
        isCostCentreMode={budgetPermissions.isCostCentreMode}
        costCentres={costCentres}
        hierarchicalCostCentres={hierarchicalCostCentres}
        isStructureView={isStructureView}
        projectId={projectId ?? undefined}
        onSave={async (updatedBudget, revisionData) => {
          console.log("[ProjectDetails] Saving cost budget:", updatedBudget);

          try {
            const result = await updateBudget(project.id, updatedBudget);

            if (result.success) {
              if (isCostNewRevision) {
                await createBudgetRevision({
                  projectId: project.id,
                  status: 'draft',
                  budgetLayer: 'cost',
                  reasonCategory: revisionData?.reasonCategory,
                  reasonDetail: revisionData?.reason,
                  roleBudgets: [],
                });
                toast.success("Cost budget draft revision saved");
                setCostLoadedRevision(null);
                setIsCostNewRevision(false);
                setEditCostBudgetPageOpen(false);
                setLocalBudgetData(null);
                refetchBudget();
                setShowCostRevisionHistory(true);
              } else {
                // Always create a revision entry for cost budgets so history is preserved
                try {
                  await saveRevision({
                    projectId: project.id,
                    reasonCategory: revisionData?.reasonCategory ?? undefined,
                    reasonDetail: revisionData?.reason ?? undefined,
                    status: revisionData?.status === 'draft' ? 'draft' : 'active',
                    budgetLayer: 'cost',
                    roleBudgets: [],
                  });
                } catch (revErr) {
                  console.error("[ProjectDetails] Error saving revision notes:", revErr);
                }

                toast.success("Cost budget saved successfully");
                setCostLoadedRevision(null);
                setEditCostBudgetPageOpen(false);
                refetchBudget();
              }
            } else {
              toast.error("Failed to save cost budget");
            }
          } catch (error) {
            console.error("[ProjectDetails] Error saving budget:", error);
            toast.error("Failed to save cost budget");
          }
        }}
        onMakeActive={costLoadedRevision ? () => {
          setCostMakeActiveSource('make-active');
          setCostMakeActiveRevision(costLoadedRevision);
          setEditCostBudgetPageOpen(false);
        } : undefined}
        onCancel={() => {
          setCostLoadedRevision(null);
          setIsCostNewRevision(false);
          setEditCostBudgetPageOpen(false);
        }}
      />
    );
  }

  // Show full-screen edit page if editing sales budget
  // Use enrichedBudgetData if available, otherwise use default empty budget data for new projects
  if (editSalesBudgetPageOpen) {
    const budgetDataToUse = enrichedBudgetData || getDefaultBudgetData();
    const projectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || `Project ${projectId}`;
    const budgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: projectDisplayName,
    };

    // Fixed Price / Retainer → use the dedicated FP page
    if (project?.type === 'fixed-price' || project?.type === 'retainer') {
      return (
        <EditSalesBudgetPageFP
          budgetData={budgetDataWithCorrectName}
          projectType={project.type}
          billingTriggerType={project.billingTriggerType}
          loadedFromRevision={salesLoadedRevision ?? undefined}
          isNewRevision={isSalesNewRevision}
          requireRevisionNotes={budgetPermissions.settings.revisionSettings?.requireRevisionNotesOnSalesSave}
          onSave={async (updatedBudget, revisionData) => {

            const result = await updateBudget(project.id, updatedBudget);

            if (result.success) {
              if (isSalesNewRevision) {
                await createBudgetRevision({
                  projectId: project.id,
                  status: 'draft',
                  budgetLayer: 'sales',
                  reasonCategory: revisionData?.reasonCategory,
                  reasonDetail: revisionData?.reason,
                  roleBudgets: [],
                });
                toast.success("Sales budget draft revision saved");
                setSalesLoadedRevision(null);
                setIsSalesNewRevision(false);
                setEditSalesBudgetPageOpen(false);
                setLocalBudgetData(null);
                // Only the sales layer changed — reuse cost/resource data.
                refetchBudget({ layers: ['sales'] });
                setShowSalesRevisionHistory(true);
              } else {
                try {
                  await saveRevision({
                    projectId: project.id,
                    reasonCategory: revisionData?.reasonCategory ?? undefined,
                    reasonDetail: revisionData?.reason ?? undefined,
                    status: revisionData?.status === 'draft' ? 'draft' : 'active',
                    budgetLayer: 'sales',
                    roleBudgets: [],
                  });
                } catch (revErr) {
                  console.error("[ProjectDetails] Error saving revision notes:", revErr);
                }

                toast.success("Sales budget saved successfully");
                setSalesLoadedRevision(null);
                setEditSalesBudgetPageOpen(false);
                // Only the sales layer changed — reuse cost/resource data.
                refetchBudget({ layers: ['sales'] });
              }
            } else {
              throw new Error(result.error || "Failed to save sales budget");
            }
          }}
          onMakeActive={salesLoadedRevision ? () => {
            setSalesMakeActiveSource('make-active');
            setSalesMakeActiveRevision(salesLoadedRevision);
            setEditSalesBudgetPageOpen(false);
          } : undefined}
          onCancel={() => {
            setSalesLoadedRevision(null);
            setIsSalesNewRevision(false);
            setEditSalesBudgetPageOpen(false);
          }}
        />
      );
    }

    // Time & Materials → existing T&M page
    return (
      <EditSalesBudgetPage
        budgetData={budgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        loadedFromRevision={salesLoadedRevision ?? undefined}
        isNewRevision={isSalesNewRevision}
        resourceBudget={budgetDataToUse.resourceBudget}
        requireRevisionNotes={budgetPermissions.settings.revisionSettings?.requireRevisionNotesOnSalesSave}
        cascadeTargetCategoryId={budgetPermissions.settings.revisionSettings?.cascadeTargetCategoryId ?? null}
        isCostCentreMode={budgetPermissions.isCostCentreMode}
        costCentres={costCentres}
        hierarchicalCostCentres={hierarchicalCostCentres}
        isStructureView={isStructureView}
        onSave={async (updatedBudget, revisionData) => {

          const result = await updateBudget(project.id, updatedBudget);

          if (result.success) {
            if (isSalesNewRevision) {
              await createBudgetRevision({
                projectId: project.id,
                status: 'draft',
                budgetLayer: 'sales',
                reasonCategory: revisionData?.reasonCategory,
                reasonDetail: revisionData?.reason,
                roleBudgets: [],
              });
              toast.success("Sales budget draft revision saved");
              setSalesLoadedRevision(null);
              setIsSalesNewRevision(false);
              setEditSalesBudgetPageOpen(false);
              setLocalBudgetData(null);
              // Only the sales layer changed — reuse cost/resource data.
              refetchBudget({ layers: ['sales'] });
              setShowSalesRevisionHistory(true);
            } else {
              try {
                await saveRevision({
                  projectId: project.id,
                  reasonCategory: revisionData?.reasonCategory ?? undefined,
                  reasonDetail: revisionData?.reason ?? undefined,
                  status: revisionData?.status === 'draft' ? 'draft' : 'active',
                  budgetLayer: 'sales',
                  roleBudgets: [],
                });
              } catch (revErr) {
                console.error("[ProjectDetails] Error saving revision notes:", revErr);
              }

              toast.success("Sales budget saved successfully");
              setSalesLoadedRevision(null);
              setEditSalesBudgetPageOpen(false);
              // Only the sales layer changed — reuse cost/resource data.
              refetchBudget({ layers: ['sales'] });
            }
          } else {
            throw new Error(result.error || "Failed to save sales budget");
          }
        }}
        onMakeActive={salesLoadedRevision ? () => {
          setSalesMakeActiveSource('make-active');
          setSalesMakeActiveRevision(salesLoadedRevision);
          setEditSalesBudgetPageOpen(false);
        } : undefined}
        onCancel={() => {
          setSalesLoadedRevision(null);
          setIsSalesNewRevision(false);
          setEditSalesBudgetPageOpen(false);
        }}
      />
    );
  }

  // Show full-screen new revision screen — pre-loaded with existing resource budget data
  if (newRevisionPageOpen) {
    const baseData = enrichedBudgetData || getDefaultBudgetData();
    const projectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || baseData.projectName;

    const newRevisionBudgetData: typeof baseData = {
      ...baseData,
      projectName: projectDisplayName,
    };

    return (
      <EditResourceBudgetPage
        projectId={numericProjectId}
        budgetData={newRevisionBudgetData}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        isNewRevision
        isCostCentreMode={budgetPermissions.isCostCentreMode}
        costCentres={costCentres}
        hierarchicalCostCentres={hierarchicalCostCentres}
        isStructureView={isStructureView}
        onSave={async (updatedBudget) => {
          const roles = updatedBudget.resourceBudget?.byRole ?? [];
          await createBudgetRevision({
            projectId: project.id,
            status: 'draft',
            roleBudgets: roles.map(role => ({
              roleId: role.roleId,
              roleName: role.role,
              budgetHours: role.budgetHours,
              billRate: role.billRate,
              costRate: role.costRate,
              budgetCost: role.budgetCost,
              monthlyHours: role.monthlyHours ? JSON.stringify(role.monthlyHours) : undefined,
            })),
          });
          refetchBudget();
          setNewRevisionPageOpen(false);
          setRevisionHistoryOpen(true);
        }}
        onCancel={() => {
          setNewRevisionPageOpen(false);
          setRevisionHistoryOpen(true);
        }}
        onMakeActive={() => {
          setMakeActiveSource('make-active');
          setNewRevisionPageOpen(false);
        }}
      />
    );
  }

  // Show full-screen duplicate revision screen — pre-loaded with current resource budget data
  // (mirrors Cost/Sales Budget pattern: current data + loadedFromRevision banner)
  if (duplicateRevision) {
    const baseData = enrichedBudgetData || getDefaultBudgetData();
    const projectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || baseData.projectName;

    // Use snapshot data if available, otherwise fall back to current budget data
    const snapshotByRole = duplicateRevision.resourceSnapshot?.length
      ? duplicateRevision.resourceSnapshot.map((snap, idx) => ({
          id: snap.roleId ?? idx,
          roleId: snap.roleId,
          role: snap.role,
          budgetHours: snap.budgetHours,
          allocatedHours: snap.budgetHours,
          actualHours: 0,
          remainingHours: snap.budgetHours,
          percentUsed: 0,
          billRate: snap.billRate,
          costRate: snap.costRate,
          budgetCost: snap.budgetCost,
          actualCost: 0,
          monthlyHours: snap.monthlyHours ?? {},
        }))
      : baseData.resourceBudget?.byRole ?? [];

    const duplicateBudgetData: typeof baseData = {
      ...baseData,
      projectName: projectDisplayName,
      resourceBudget: {
        ...baseData.resourceBudget,
        byRole: snapshotByRole,
      },
    };

    return (
      <EditResourceBudgetPage
        projectId={numericProjectId}
        budgetData={duplicateBudgetData}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        loadedFromRevision={duplicateRevision}
        isCostCentreMode={budgetPermissions.isCostCentreMode}
        costCentres={costCentres}
        hierarchicalCostCentres={hierarchicalCostCentres}
        isStructureView={isStructureView}
        onSave={async (updatedBudget) => {
          const roles = updatedBudget.resourceBudget?.byRole ?? [];
          await createBudgetRevision({
            projectId: project.id,
            status: 'draft',
            roleBudgets: roles.map(role => ({
              roleId: role.roleId,
              roleName: role.role,
              budgetHours: role.budgetHours,
              billRate: role.billRate,
              costRate: role.costRate,
              budgetCost: role.budgetCost,
              monthlyHours: role.monthlyHours ? JSON.stringify(role.monthlyHours) : undefined,
            })),
          });
          refetchBudget();
          setDuplicateRevision(null);
          setRevisionHistoryOpen(true);
        }}
        onCancel={() => {
          setDuplicateRevision(null);
          setRevisionHistoryOpen(true);
        }}
        onMakeActive={() => {
          setMakeActiveSource('make-active');
          setDuplicateRevision(null);
        }}
      />
    );
  }

  // Show full-screen view of a historical resource budget revision
  // (mirrors Cost/Sales Budget pattern: current data + loadedFromRevision banner)
  if (viewingRevisionPageOpen && viewingRevision) {
    const baseData = enrichedBudgetData || getDefaultBudgetData();
    const projectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || baseData.projectName;

    // Use snapshot data if available, otherwise fall back to current budget data
    const snapshotByRole = viewingRevision.resourceSnapshot?.length
      ? viewingRevision.resourceSnapshot.map((snap, idx) => ({
          id: snap.roleId ?? idx,
          roleId: snap.roleId,
          role: snap.role,
          budgetHours: snap.budgetHours,
          allocatedHours: snap.budgetHours,
          actualHours: 0,
          remainingHours: snap.budgetHours,
          percentUsed: 0,
          billRate: snap.billRate,
          costRate: snap.costRate,
          budgetCost: snap.budgetCost,
          actualCost: 0,
          monthlyHours: snap.monthlyHours ?? {},
        }))
      : baseData.resourceBudget?.byRole ?? [];

    const viewBudgetData: typeof baseData = {
      ...baseData,
      projectName: projectDisplayName,
      resourceBudget: {
        ...baseData.resourceBudget,
        byRole: snapshotByRole,
      },
    };

    return (
      <EditResourceBudgetPage
        projectId={numericProjectId}
        budgetData={viewBudgetData}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        loadedFromRevision={viewingRevision}
        isCostCentreMode={budgetPermissions.isCostCentreMode}
        costCentres={costCentres}
        hierarchicalCostCentres={hierarchicalCostCentres}
        isStructureView={isStructureView}
        onSave={async (updatedBudget) => {
          const roles = updatedBudget.resourceBudget?.byRole ?? [];
          await createBudgetRevision({
            projectId: project.id,
            status: 'draft',
            roleBudgets: roles.map(role => ({
              roleId: role.roleId,
              roleName: role.role,
              budgetHours: role.budgetHours,
              billRate: role.billRate,
              costRate: role.costRate,
              budgetCost: role.budgetCost,
              monthlyHours: role.monthlyHours ? JSON.stringify(role.monthlyHours) : undefined,
            })),
          });
          refetchBudget();
          setViewingRevisionPageOpen(false);
          setViewingRevision(null);
        }}
        onCancel={() => {
          setViewingRevisionPageOpen(false);
          setViewingRevision(null);
        }}
        onMakeActive={viewingRevision?.status !== 'current' && viewingRevision?.status !== 'active' ? () => {
          setMakeActiveSource('make-active');
          setMakeActiveRevision(viewingRevision);
        } : undefined}
      />
    );
  }

  // Show full-screen edit page if editing resource budget
  // Use enrichedBudgetData if available, otherwise use default empty budget data for new projects
  if (editResourceBudgetPageOpen) {
    const budgetDataToUse = enrichedBudgetData || getDefaultBudgetData();
    // Override projectName with actual project name/code instead of the placeholder from the API
    const resourceProjectDisplayName = project?.code
      ? `${project.code} - ${project.name}`
      : project?.name || budgetDataToUse.projectName;
    const resourceBudgetDataWithCorrectName = {
      ...budgetDataToUse,
      projectName: resourceProjectDisplayName,
    };
    return (
      <EditResourceBudgetPage
        projectId={numericProjectId}
        budgetData={resourceBudgetDataWithCorrectName}
        projectStartDate={project?.startDate}
        projectEndDate={project?.endDate}
        isCostCentreMode={budgetPermissions.isCostCentreMode}
        costCentres={costCentres}
        hierarchicalCostCentres={hierarchicalCostCentres}
        isStructureView={isStructureView}
        onSave={async (updatedBudget, revisionData) => {
          console.log("[ProjectDetails] Saving resource budget with revision:", { updatedBudget, revisionData });

          try {
            // 1. Save the resource budget (roles, hours, etc.)
            const result = await updateBudget(project.id, updatedBudget);

            if (!result.success) {
              toast.error("Failed to save resource budget");
              return;
            }

            // 2. Save the budget revision record
            const roles = updatedBudget.resourceBudget?.byRole ?? enrichedBudgetData?.resourceBudget?.byRole ?? [];
            const revisionPayload = {
              projectId: project.id,
              budgetLayer: 'resource' as const,
              budgetTarget: 'category' as const,
              reasonCategory: revisionData?.reasonCategory as string | undefined,
              reasonCategoryLabel: revisionData?.reasonCategoryLabel ?? (revisionData?.reasonCategory as string | undefined),
              reasonDetail: revisionData?.reason,
              status: (revisionData?.status === 'draft' ? 'draft' : 'active') as 'active' | 'draft',
              roleBudgets: roles.map(role => ({
                roleId: role.roleId,
                roleName: role.role,
                budgetHours: role.budgetHours,
                billRate: role.billRate,
                costRate: role.costRate,
                budgetCost: role.budgetCost,
                monthlyHours: role.monthlyHours ? JSON.stringify(role.monthlyHours) : undefined,
              })),
              cascadeDecision: revisionData?.cascadeRecord ? {
                updateCostLabour: revisionData.cascadeRecord.costBudgetUpdated,
                updateSalesLabour: revisionData.cascadeRecord.salesBudgetUpdated,
                overriddenByUser: revisionData.cascadeRecord.overriddenByUser,
                cascadeTargetCategoryId: revisionData.cascadeRecord.cascadeTargetCategoryId,
                cascadeTargetCategoryName: revisionData.cascadeRecord.cascadeTargetCategoryName,
              } : undefined,
            };

            await saveRevision(revisionPayload);
            console.log("[ProjectDetails] Revision saved successfully");

            // 5.14 — Cost Centre mode cascade: the backend ExecuteCostCentreCascadeAsync
            // persists cascade results server-side. The refetchBudget() call below will
            // pull the updated CC budgets from the API for the tab views.

            toast.success("Resource budget saved successfully");
            setEditResourceBudgetPageOpen(false);
            refetchBudget();
          } catch (error) {
            console.error("[ProjectDetails] Error saving budget:", error);
            toast.error("Failed to save resource budget");
          }
        }}
        onCancel={() => setEditResourceBudgetPageOpen(false)}
      />
    );
  }

  return (
    <ProjectDetailsContext.Provider value={ctx}>
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4" data-tour="project-details-header">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (onBack ? onBack() : navigate(ROUTES.PROJECTS))}
            className="h-8 w-8 mt-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            {/* Inline-editable project name */}
            {canPerformAction('edit') && isNameEditing ? (
              <div className="space-y-1">
                <div className="flex items-baseline">
                  {project.code && (
                    <span className="text-xl font-bold leading-tight whitespace-nowrap">{project.code} -&nbsp;</span>
                  )}
                  <input
                    autoFocus
                    type="text"
                    value={inlineNameValue}
                    onChange={(e) => setInlineNameValue(e.target.value)}
                    onBlur={() => void handleNameSave()}
                    onKeyDown={handleNameKeyDown}
                    maxLength={100}
                    className="flex-1 min-w-0 text-xl font-bold bg-transparent border-0 border-b-2 border-primary outline-none focus:ring-0 px-0 py-0.5 leading-tight"
                  />
                </div>
                {/* Validation feedback while editing */}
                {inlineNameValue.trim().length > 0 && inlineNameValue.trim().length < 3 && (
                  <p className="text-xs text-error-600">Project name must be at least 3 characters.</p>
                )}
                {projectNameHasIssuedInvoices && inlineNameValue.trim() !== project.name && (
                  <p className="flex items-center gap-1 text-xs text-warning-700">
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    This name may already appear on sent invoices. Changing it won&apos;t update existing documents.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">Press Enter to save · Esc to cancel</p>
              </div>
            ) : canPerformAction('edit') ? (
              <div className="flex items-baseline">
                {project.code && (
                  <span className="text-xl font-bold leading-tight select-text cursor-text whitespace-nowrap">{project.code} -&nbsp;</span>
                )}
                <button
                  type="button"
                  onClick={handleNameEditStart}
                  className="group flex items-center gap-1.5 text-left min-w-0"
                  title="Click to edit project name"
                >
                  <span className="text-xl font-bold leading-tight truncate">
                    {project.name}
                  </span>
                  <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </button>
              </div>
            ) : (
              <h1 className="text-xl font-bold leading-tight truncate">
                {project.code ? `${project.code} - ${project.name}` : project.name}
              </h1>
            )}

            {/* Inline-editable description */}
            {canPerformAction('edit') && isDescEditing ? (
              <div className="mt-1 space-y-1">
                <textarea
                  autoFocus
                  value={inlineDescValue}
                  onChange={(e) => setInlineDescValue(e.target.value)}
                  onBlur={() => void handleDescSave()}
                  onKeyDown={handleDescKeyDown}
                  maxLength={500}
                  rows={2}
                  className="w-full text-sm text-muted-foreground bg-transparent border-0 border-b border-primary outline-none focus:ring-0 px-0 py-0.5 resize-none leading-relaxed"
                  placeholder="Add a description..."
                />
                <p className="text-xs text-muted-foreground">Ctrl+Enter to save · Esc to cancel</p>
              </div>
            ) : canPerformAction('edit') ? (
              <button
                type="button"
                onClick={handleDescEditStart}
                className="group flex items-center gap-1 mt-0.5 text-left max-w-prose"
                title="Click to edit description"
              >
                <span className="text-sm text-muted-foreground leading-relaxed">
                  {project.description || <span className="italic opacity-60">Add a description…</span>}
                </span>
                <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity shrink-0 mt-0.5" />
              </button>
            ) : (
              <span className="text-sm text-muted-foreground leading-relaxed mt-0.5">
                {project.description || <span className="italic opacity-60">No description</span>}
              </span>
            )}
          </div>
        </div>
        {/* Inline Status Select + Health Badge */}
        <div className="flex items-center gap-3" data-tour="project-details-actions">
          {/* Project Health Badge — read-only, server-derived (project.health) with a
              per-dimension tooltip. The badge no longer waits on client-side timesheet
              loading because the server rollup already includes realisation rate. */}
          {enrichedProjectHealth && enrichedHealthBreakdown && (() => {
            const healthLabel = enrichedProjectHealth === 'green' ? 'Healthy' : enrichedProjectHealth === 'amber' ? 'At Risk' : 'Critical';
            const signalDot: Record<string, string> = { green: '🟢', amber: '🟡', red: '🔴' };
            return (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant={healthToVariant(enrichedProjectHealth)}
                      className="cursor-pointer select-none gap-1.5"
                      disableHover
                    >
                      <StatusIndicator variant={enrichedProjectHealth === 'green' ? 'success' : enrichedProjectHealth === 'amber' ? 'warning' : 'error'} size="sm" />
                      {healthLabel}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="end" className="p-0 w-64">
                    <div className="px-3 py-2 border-b">
                      <p className="text-xs font-semibold text-foreground">Health Breakdown</p>
                      <p className="text-xs text-muted-foreground">Worst signal across all dimensions</p>
                    </div>
                    <div className="px-3 py-2 space-y-1.5">
                      {enrichedHealthBreakdown.dimensions.map((dim) => (
                        <div key={dim.label} className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-xs leading-none">{signalDot[dim.signal]}</span>
                            <span className="text-xs font-medium text-foreground truncate">{dim.label}</span>
                          </div>
                          <span className="text-xs text-muted-foreground text-right shrink-0">{dim.detail}</span>
                        </div>
                      ))}
                      {enrichedHealthBreakdown.dimensions.length === 0 && (
                        <p className="text-xs text-muted-foreground">No dimensions evaluated</p>
                      )}
                    </div>
                    <div className="px-3 py-1.5 border-t">
                      <p className="text-xs text-muted-foreground">Milestones &amp; realisation shown in detail view only</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })()}
          {canPerformAction('changeStatus') ? (
            <Select
              value={String(project.statusId)}
              onValueChange={handleInlineStatusChange}
              disabled={isStatusSaving}
            >
              <SelectTrigger
                className={cn(
                  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                  "h-auto w-auto shadow-none focus:ring-0 gap-1",
                  "[&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-70 [&>svg]:shrink-0",
                  getProjectStatusColor(project.statusId, 'badge'),
                )}
              >
                {isStatusSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : null}
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(selectableStatuses.length > 0
                  ? selectableStatuses
                  : Object.entries(PROJECT_STATUS_LABELS).map(([id, label]) => ({
                      id: Number(id),
                      name: label,
                    }))
                ).map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge
              className={cn(
                "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                getProjectStatusColor(project.statusId, 'badge'),
              )}
              disableHover
            >
              {PROJECT_STATUS_LABELS[project.statusId as keyof typeof PROJECT_STATUS_LABELS] ?? `Status ${project.statusId}`}
            </Badge>
          )}

        </div>
      </div>

      {/* Tabs */}
      <TooltipProvider>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div ref={tabsContainerCallbackRef} className="w-full" data-tour="project-details-tabs">
            <TabsList className="w-full justify-start">
              {visibleTabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2 flex-none">
                  {createElement(tab.IconComponent, { className: "h-4 w-4" })}
                  {tab.label}
                </TabsTrigger>
              ))}

              {pinnedMoreTab && !visibleTabs.some(t => t.id === pinnedMoreTab.id) && (
                <TabsTrigger value={pinnedMoreTab.id} className="flex items-center gap-2 flex-none">
                  {createElement(pinnedMoreTab.IconComponent, { className: "h-4 w-4" })}
                  {pinnedMoreTab.label}
                </TabsTrigger>
              )}

              {moreTabs.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted">
                      <MoreHorizontal className="h-4 w-4" />
                      More
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {moreTabs
                      .filter(tab => !pinnedMoreTab || tab.id !== pinnedMoreTab.id)
                      .map((tab) => (
                        <DropdownMenuItem
                          key={tab.id}
                          onClick={() => {
                            setPinnedMoreTab(tab);
                            setActiveTab(tab.id);
                          }}
                          className="flex items-center gap-2"
                        >
                          {createElement(tab.IconComponent, { className: "h-4 w-4" })}
                          {tab.label}
                        </DropdownMenuItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setTabSettingsOpen(true)}
                    className="inline-flex items-center justify-center p-1.5 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted ml-auto"
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Configure Tabs</TooltipContent>
              </Tooltip>
            </TabsList>
          </div>

          {/* Tab Content Container */}
          <div>
              {/* Overview Tab */}

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4" data-tour="project-details-overview">
                <OverviewTab />
              </TabsContent>

              {/* Financials Tab */}
              <TabsContent value="financials" className="space-y-4" data-tour="project-details-financials">
                <FinancialsTab />
              </TabsContent>

              {/* Billing Tab */}
              <TabsContent value="billing" className="space-y-4" data-tour="project-details-billing">
                <BillingTab />
              </TabsContent>

              {/* Sales Budget Tab */}
              <TabsContent value="sales-budget" className="space-y-4" data-tour="project-details-sales-budget">
                <SalesBudgetTab />
              </TabsContent>

              {/* Cost Budget Tab */}
              <TabsContent value="cost-budget" className="space-y-4" data-tour="project-details-cost-budget">
                <CostBudgetTab />
              </TabsContent>

              {/* Resource Budget Tab */}
              <TabsContent value="resource-budget" className="space-y-4" data-tour="project-details-resource-budget">
                <ResourceBudgetTab />
              </TabsContent>

              {/* Cost Centres Tab */}
              <TabsContent value="cost-centres" className="space-y-4" data-tour="project-details-cost-centres">
                <CostCentresTab />
              </TabsContent>

              {/* Purchases Tab */}
              <TabsContent value="purchases" className="space-y-4" data-tour="project-details-purchases">
                <PurchasesTab />
              </TabsContent>

              {/* Team Tab */}
              <TabsContent value="team" className="space-y-4" data-tour="project-details-team">
                <TeamTab />
              </TabsContent>

              {/* Resourcing Tab */}
              <TabsContent value="resourcing" className="space-y-4" data-tour="project-details-resourcing">
                <ResourcingTab />
              </TabsContent>


              {/* Time Tab */}
              <TabsContent value="time" className="space-y-4" data-tour="project-details-time">
                <TimeTab />
              </TabsContent>

              {/* Expenses Tab */}
              <TabsContent value="expenses" className="space-y-4" data-tour="project-details-expenses">
                <ExpensesTab />
              </TabsContent>


              {/* Audit Tab */}
              <TabsContent value="audit" className="space-y-4" data-tour="project-details-audit">
                <AuditTab />
              </TabsContent>
              <TabsContent value="reports" className="space-y-4" data-tour="project-details-reports">
                <ProjectReportsTab projectId={numericProjectId ?? 0} />
              </TabsContent>
          </div>
        </Tabs>
      </TooltipProvider>

      {/* Tab Settings Dialog */}
      <TabSettingsDialog
        open={tabSettingsOpen}
        onOpenChange={setTabSettingsOpen}
        availableTabs={tabConfigsForDialog}
        selectedTabIds={selectedTabIds}
        onSave={saveTabOrder}
        visibleTabCount={visibleTabs.length}
      />

      {/* Configure Structure Modal */}
      <ConfigureStructureModal
        open={configureStructureOpen}
        onOpenChange={setConfigureStructureOpen}
        projectName={project?.name || ""}
        costCentres={hierarchicalCostCentres}
        onSave={async (updatedStructure) => {
          if (!numericProjectId) {
            throw new Error("Project ID is required to save the cost centre structure.");
          }
          const savedStructure = await saveCostCentreStructure(numericProjectId, updatedStructure);
          setCostCentres(savedStructure.filter(cc => !cc.isStructureNode));
          setHierarchicalCostCentres(savedStructure);
        }}
      />

      {/* Cost Centre Details Panel */}
      <CostCentreDetailsPanel
        costCentre={selectedCostCentre}
        projectId={numericProjectId}
        projectStartDate={project?.startDate}
        open={costCentreDetailsPanelOpen}
        onOpenChange={setCostCentreDetailsPanelOpen}
        onEdit={async (updatedCostCentre) => {
          // Optimistic update, then refetch for consistency
          setCostCentres((prev) => prev.map((cc) => (cc.id === updatedCostCentre.id ? updatedCostCentre : cc)));
          setHierarchicalCostCentres((prev) =>
            prev.map((cc) => (cc.id === updatedCostCentre.id ? { ...cc, ...updatedCostCentre } : cc)),
          );
          if (selectedCostCentre?.id === updatedCostCentre.id) {
            setSelectedCostCentre(updatedCostCentre);
          }
          await refetchCostCentres();
        }}
        onDelete={async (costCentreId) => {
          setCostCentres((prev) => prev.filter((cc) => cc.id !== costCentreId));
          setHierarchicalCostCentres((prev) => prev.filter((cc) => cc.id !== costCentreId));
          await refetchCostCentres();
        }}
        onMarkInactive={async (costCentreId) => {
          const cc = costCentres.find((c) => c.id === costCentreId);
          if (!cc) return;
          const updated = { ...cc, isActive: false };
          setCostCentres((prev) => prev.map((c) => (c.id === costCentreId ? updated : c)));
          setHierarchicalCostCentres((prev) =>
            prev.map((c) => (c.id === costCentreId ? { ...c, isActive: false } : c)),
          );
          if (getDataMode() === 'live') {
            try {
              await updateCostCentre(costCentreId, {
                id: cc.id,
                projectId: numericProjectId,
                code: cc.code,
                name: cc.name,
                type: cc.type,
                status: cc.status,
                billable: cc.billable,
                owner: cc.owner,
                description: cc.description,
                costsBudget: cc.costsBudget,
                actualCosts: cc.actualCosts,
                committedCosts: cc.committedCosts,
                revenueBudget: cc.revenueBudget,
                actualRevenue: cc.actualRevenue,
                hoursBudget: cc.hoursBudget,
                actualHours: cc.actualHours,
                isActive: false,
              });
            } catch (error) {
              console.error('Failed to mark cost centre as inactive:', error);
              setCostCentres((prev) => prev.map((c) => (c.id === costCentreId ? cc : c)));
              return;
            }
          }
          if (selectedCostCentre?.id === costCentreId) {
            setSelectedCostCentre(updated);
          }
          toast.success('Cost centre marked as inactive');
          await refetchCostCentres();
        }}
        onExpandToFullView={() => {
          // Close the side drawer and open the full modal
          setCostCentreDetailsPanelOpen(false);
          setCostCentreFullModalOpen(true);
        }}
      />

      {/* Cost Centre Column Configurator */}
      <ColumnConfiguratorDrawer
        open={costCentreColumnConfigOpen}
        onOpenChange={setCostCentreColumnConfigOpen}
        columns={(budgetPermissions.isCostCentreMode
          ? costCentreColumns
          : costCentreColumns.filter(col => !BUDGET_ONLY_COLUMN_KEYS.includes(col.key as string))
        ).map((col, index): ColumnDefinition => ({
          id: String(col.key),
          name: col.label,
          label: col.label,
          description: '',
          category: col.category as ColumnDefinition['category'],
          visible: col.visible,
          order: index,
          align: col.align === 'center' ? undefined : col.align,
        }))}
        onSave={(newColumns) => {
          // Map shared ColumnDefinition[] back to CostCentreColumnDefinition[]
          const mapped = newColumns.map((col) => {
            const original = costCentreColumns.find((c) => String(c.key) === col.id);
            return {
              key: col.id as typeof costCentreColumns[number]['key'],
              label: col.name,
              visible: col.visible,
              sortable: original?.sortable ?? true,
              align: original?.align ?? 'left',
              category: col.category as typeof costCentreColumns[number]['category'],
            };
          });
          const merged = budgetPermissions.isCostCentreMode
            ? mapped
            : [...mapped, ...costCentreColumns.filter(col => BUDGET_ONLY_COLUMN_KEYS.includes(col.key as string))];
          setCostCentreColumns(merged);
          localStorage.setItem('cost-centre-column-preferences', JSON.stringify(merged));
          const mode = getDataMode();
          if (mode === 'live') {
            saveColumnPreferences(merged as unknown as Parameters<typeof saveColumnPreferences>[0], 'cost-centre-column-preferences').catch((error) => {
              console.error('Failed to save cost centre column preferences to API:', error);
            });
          }
        }}
      />

      {/* Create Cost Centre Panel */}
      <CreateCostCentrePanel
        open={createCostCentrePanelOpen}
        onOpenChange={setCreateCostCentrePanelOpen}
        existingCodes={[...costCentres.map((cc) => cc.code), ...hierarchicalCostCentres.map((cc) => cc.code)]}
        defaultStatus={(() => {
          const statusMap: Record<number, 'Not Started' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled'> = {
            1: 'Not Started',   // Planning
            2: 'In Progress',   // In Progress
            3: 'On Hold',       // On Hold
            4: 'In Progress',   // Reopened
            5: 'Cancelled',     // Cancelled
            6: 'Completed',     // Completed
            7: 'Not Started',   // Opportunity
          };
          return project?.statusId ? statusMap[project.statusId] || 'Not Started' : 'Not Started';
        })()}
        onSave={async (newCostCentre) => {
          const mode = getDataMode();

          if (mode === "live" && projectId) {
            try {
              const createdCostCentre = await createCostCentre({
                projectId: numericProjectId,
                code: newCostCentre.code,
                name: newCostCentre.name,
                type: newCostCentre.type,
                status: newCostCentre.status,
                billable: newCostCentre.billable,
                owner: newCostCentre.owner,
                description: newCostCentre.description,
                costsBudget: newCostCentre.costsBudget,
                revenueBudget: newCostCentre.revenueBudget,
                hoursBudget: newCostCentre.hoursBudget,
              });

              // Optimistic update, then refetch for consistency
              const costCentreWithId: CostCentre = {
                ...newCostCentre,
                id: createdCostCentre.id || -Date.now(),
              };

              setCostCentres((prev) => [...prev, costCentreWithId]);
              setHierarchicalCostCentres((prev) => [...prev, { ...costCentreWithId, children: [] }]);
              await refetchCostCentres();
            } catch (error) {
              console.error("Failed to create cost centre:", error);
              const id = -Date.now();
              const costCentreWithId: CostCentre = {
                ...newCostCentre,
                id,
              };
              setCostCentres((prev) => [...prev, costCentreWithId]);
              setHierarchicalCostCentres((prev) => [...prev, { ...costCentreWithId, children: [] }]);
            }
          } else {
            const id = -Date.now();
            const costCentreWithId: CostCentre = {
              ...newCostCentre,
              id,
            };

            setCostCentres((prev) => [...prev, costCentreWithId]);
            setHierarchicalCostCentres((prev) => [...prev, { ...costCentreWithId, children: [] }]);
          }
        }}
      />

      {/* Cost Centre Full Modal */}
      <CostCentreFullModal
        costCentre={selectedCostCentre}
        projectId={numericProjectId}
        projectStartDate={project?.startDate}
        allCostCentres={costCentres}
        open={costCentreFullModalOpen}
        onOpenChange={setCostCentreFullModalOpen}
        onCostCentreChange={async (costCentre) => {
          setSelectedCostCentre(costCentre);
          await refetchCostCentres();
        }}
        onDelete={async (costCentreId) => {
          setCostCentres((prev) => prev.filter((cc) => cc.id !== costCentreId));
          setHierarchicalCostCentres((prev) => prev.filter((cc) => cc.id !== costCentreId));
          await refetchCostCentres();
        }}
        onMinimize={() => {
          setCostCentreFullModalOpen(false);
          setCostCentreDetailsPanelOpen(true);
        }}
      />

      {/* PM Reassignment Confirmation Dialog */}
      <AlertDialog open={showPmConfirmDialog} onOpenChange={setShowPmConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reassign Project Manager?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reassign this project to{" "}
              <strong>{pendingPmChange?.employee.name}</strong>?
              The previous project manager will lose their primary project association.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelPmChange}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmPmChange}>Confirm Reassignment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AM Reassignment Confirmation Dialog */}
      <AlertDialog open={showAmConfirmDialog} onOpenChange={setShowAmConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reassign Account Manager?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to assign{" "}
              <strong>{pendingAmChange?.employee.name}</strong> as the Account Manager for this project?
              {accountManager && (
                <> <strong>{accountManager.name}</strong> will no longer be the Account Manager.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelAmChange}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmAmChange}>Confirm Reassignment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancelUnified}>Discard Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Budget Edit Modals */}
      {budgetData && (
        <>
          <EditBudgetModal
            open={salesBudgetEditModalOpen}
            onOpenChange={setSalesBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type ?? "fixed-price"}
            billingTriggerType={project?.billingTriggerType}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="sales"
          />

          <EditBudgetModal
            open={costBudgetEditModalOpen}
            onOpenChange={setCostBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type ?? "fixed-price"}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="cost"
          />

          <EditBudgetModal
            open={resourceBudgetEditModalOpen}
            onOpenChange={setResourceBudgetEditModalOpen}
            budgetData={budgetData}
            projectType={project?.type ?? "fixed-price"}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSave={handleSaveBudget}
            initialTab="resource"
          />

          <BudgetExportModal
            open={budgetExportModalOpen}
            onOpenChange={setBudgetExportModalOpen}
            budgetData={{
              ...budgetData,
              projectName: project?.code
                ? `${project.code} - ${project.name}`
                : project?.name || budgetData.projectName,
            }}
          />

          {/* Resource Request Modal — Resource Scheduling integration */}
          <ResourceRequestModal
            open={raiseRequestModalOpen}
            onOpenChange={setRaiseRequestModalOpen}
            prefilledRole={raiseRequestPrefilledRole}
            costCentre={raiseRequestCostCentre}
            projectName={project?.name}
            projectCode={project?.code}
            projectId={projectId}
            projectStartDate={project?.startDate}
            projectEndDate={project?.endDate}
            onSubmitSuccess={handleRequestSubmitSuccess}
            roles={liveRoleCoverage}
          />

          {/* ── Revision History AlertDialogs ──────────────────────────────────────
              Rendered at top level (outside Sheets) to avoid the Radix UI issue
              where opening an AlertDialog inside a Sheet triggers the Sheet's
              onOpenChange(false), causing both to disappear simultaneously.
          ─────────────────────────────────────────────────────────────────────── */}

          {/* Resource Budget — Make Active / Restore */}
          <AlertDialog open={!!makeActiveRevision && !showMakeActiveCascadeTarget} onOpenChange={(open) => { if (!open) setMakeActiveRevision(null); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {makeActiveSource === 'restore' ? 'Restore This Revision?' : 'Make Revision Active?'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {makeActiveSource === 'restore'
                    ? <>You are restoring <strong>RB-v{makeActiveRevision?.revisionNumber}</strong> as the active revision. The current active revision will be moved to history and this snapshot will become the live budget. This cannot be undone.</>
                    : <>This will make <strong>RB-v{makeActiveRevision?.revisionNumber}</strong> the active revision, replacing the current one. The previous active revision will be moved to history. This cannot be undone.</>
                  }
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-[var(--accent-success-500)] hover:bg-[var(--accent-success-600)] text-white"
                  onClick={() => {
                    if (makeActiveRevision && enrichedBudgetData) {
                      const revisionSettings = budgetPermissions.settings.revisionSettings;
                      const shouldCascadeCost = revisionSettings?.autoUpdateCostLabour ?? false;
                      const shouldCascadeSales = revisionSettings?.autoUpdateSalesLabour ?? false;

                      if ((shouldCascadeCost || shouldCascadeSales) && !budgetPermissions.isCostCentreMode) {
                        setPendingMakeActiveRevision(makeActiveRevision);
                        setMakeActiveRevision(null);
                        setShowMakeActiveCascadeTarget(true);
                      } else {
                        executeMakeActiveCascade(null);
                      }
                    }
                  }}
                >
                  {makeActiveSource === 'restore' ? 'Restore' : 'Make Active'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Cascade Target Resolution Dialog for Make Active flow */}
          {budgetPermissions.settings.revisionSettings && (
            <CascadeTargetResolutionDialog
              open={showMakeActiveCascadeTarget}
              cascadeSettings={budgetPermissions.settings.revisionSettings}
              onResolved={(targetCategoryId) => {
                setShowMakeActiveCascadeTarget(false);
                executeMakeActiveCascade(targetCategoryId);
              }}
              onCancel={() => {
                setShowMakeActiveCascadeTarget(false);
                setPendingMakeActiveRevision(null);
              }}
            />
          )}

          {/* Cost Budget — Make Active / Restore */}
          <AlertDialog open={!!costMakeActiveRevision} onOpenChange={(open) => { if (!open) setCostMakeActiveRevision(null); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {costMakeActiveSource === 'restore' ? 'Restore This Revision?' : 'Make Revision Active?'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {costMakeActiveSource === 'restore'
                    ? <>Restore <strong>CB-v{costMakeActiveRevision?.revisionNumber}</strong> as the active cost budget? The current active revision will become historical.</>
                    : <>Make <strong>CB-v{costMakeActiveRevision?.revisionNumber}</strong> the active cost budget? The current active revision will become historical.</>
                  }
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-[var(--accent-success-500)] hover:bg-[var(--accent-success-600)] text-white"
                  onClick={async () => {
                    if (costMakeActiveRevision) {
                      try {
                        await activateBudgetRevision(costMakeActiveRevision.id);
                        toast.success(costMakeActiveSource === 'restore'
                          ? `CB-v${costMakeActiveRevision.revisionNumber} has been restored as the active cost budget.`
                          : `CB-v${costMakeActiveRevision.revisionNumber} is now the active cost budget.`
                        );
                        refetchBudget();
                        setShowCostRevisionHistory(true);
                      } catch (error) {
                        console.error('[ProjectDetails] Failed to activate cost revision:', error);
                        toast.error('Failed to activate revision. Please try again.');
                      }
                    }
                    setCostMakeActiveRevision(null);
                  }}
                >
                  {costMakeActiveSource === 'restore' ? 'Restore' : 'Make Active'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Sales Budget — Make Active / Restore */}
          <AlertDialog open={!!salesMakeActiveRevision} onOpenChange={(open) => { if (!open) setSalesMakeActiveRevision(null); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {salesMakeActiveSource === 'restore' ? 'Restore This Revision?' : 'Make Revision Active?'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {salesMakeActiveSource === 'restore'
                    ? <>Restore <strong>SB-v{salesMakeActiveRevision?.revisionNumber}</strong> as the active sales budget? The current active revision will become historical.</>
                    : <>Make <strong>SB-v{salesMakeActiveRevision?.revisionNumber}</strong> the active sales budget? The current active revision will become historical.</>
                  }
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-[var(--accent-success-500)] hover:bg-[var(--accent-success-600)] text-white"
                  onClick={async () => {
                    if (salesMakeActiveRevision) {
                      try {
                        await activateBudgetRevision(salesMakeActiveRevision.id);
                        toast.success(salesMakeActiveSource === 'restore'
                          ? `SB-v${salesMakeActiveRevision.revisionNumber} has been restored as the active sales budget.`
                          : `SB-v${salesMakeActiveRevision.revisionNumber} is now the active sales budget.`
                        );
                        refetchBudget();
                        setShowSalesRevisionHistory(true);
                      } catch (error) {
                        console.error('[ProjectDetails] Failed to activate sales revision:', error);
                        toast.error('Failed to activate revision. Please try again.');
                      }
                    }
                    setSalesMakeActiveRevision(null);
                  }}
                >
                  {salesMakeActiveSource === 'restore' ? 'Restore' : 'Make Active'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

        </>
      )}

      {/* Billing Settings Drawer */}
      <Sheet open={billingSettingsDrawerOpen} onOpenChange={handleDrawerOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-4">
          <SheetHeader className="mb-4">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle>Billing Configuration</SheetTitle>
                <SheetDescription>Configure billing settings for this project</SheetDescription>
              </div>
              <div className="flex items-center gap-2">
                {!drawerEditMode ? (
                  <Button onClick={handleDrawerEdit} size="sm" variant="outline">
                    <Settings2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button onClick={handleDrawerCancel} size="sm" variant="outline" disabled={drawerSaving}>
                      Cancel
                    </Button>
                    <Button onClick={handleDrawerSave} size="sm" disabled={drawerSaving}>
                      {drawerSaving ? "Saving..." : "Save"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-3">
            {/* Note: This drawer shows the same settings as the Settings tab, but in a convenient side panel */}
            {project?.type === "time-and-materials" ? (
              <>
                {/* Billing Configuration Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Billing Configuration</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Billing Frequency */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-billing-frequency" className="text-base font-semibold">
                        Billing Frequency
                      </Label>
                      <Select
                        value={drawerSettings.billingFrequency}
                        onValueChange={(value) => handleDrawerSettingsChange("billingFrequency", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-billing-frequency" className="w-full">
                          <SelectValue placeholder="Select billing frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="fortnightly">Fortnightly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="on-completion">On Completion</SelectItem>
                          <SelectItem value="milestone">Milestone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator />

                    {/* Invoice Template */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-invoice-template" className="text-base font-semibold">
                        Invoice Template
                      </Label>
                      <Select
                        value={drawerSettings.invoiceTemplateId ? drawerSettings.invoiceTemplateId.toString() : (invoiceTemplates.find(t => t.isDefault)?.id || invoiceTemplates[0]?.id || '').toString()}
                        onValueChange={(value) => handleDrawerSettingsChange("invoiceTemplateId", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-invoice-template" className="w-full">
                          <SelectValue placeholder="Select invoice template" />
                        </SelectTrigger>
                        <SelectContent>
                          {invoiceTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name}{t.isDefault ? ' (Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Choose the template to use when generating invoices for this project
                      </p>
                    </div>

                    <Separator />

                    {/* Invoice Email Template */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-invoice-email-template" className="text-base font-semibold">
                        Invoice Email Template
                      </Label>
                      <Select
                        value={drawerSettings.invoiceEmailTemplateId ? drawerSettings.invoiceEmailTemplateId.toString() : (invoiceEmailTemplates.find(t => t.isDefault)?.id || invoiceEmailTemplates[0]?.id || '').toString()}
                        onValueChange={(value) => handleDrawerSettingsChange("invoiceEmailTemplateId", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-invoice-email-template" className="w-full">
                          <SelectValue placeholder="Select invoice email template" />
                        </SelectTrigger>
                        <SelectContent>
                          {invoiceEmailTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name}{t.isDefault ? ' (Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Default email template used when sending invoices for this project
                      </p>
                    </div>

                    <Separator />

                    {/* Credit Note Email Template */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-credit-note-email-template" className="text-base font-semibold">
                        Credit Note Email Template
                      </Label>
                      <Select
                        value={drawerSettings.creditNoteEmailTemplateId ? drawerSettings.creditNoteEmailTemplateId.toString() : (creditNoteEmailTemplates.find(t => t.isDefault)?.id || creditNoteEmailTemplates[0]?.id || '').toString()}
                        onValueChange={(value) => handleDrawerSettingsChange("creditNoteEmailTemplateId", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-credit-note-email-template" className="w-full">
                          <SelectValue placeholder="Select credit note email template" />
                        </SelectTrigger>
                        <SelectContent>
                          {creditNoteEmailTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name}{t.isDefault ? ' (Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Default email template used when sending credit notes for this project
                      </p>
                    </div>

                    <Separator />

                    {/* Auto Generate Draft Invoice */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="drawer-auto-generate-toggle" className="text-base font-semibold">
                            Auto Generate Draft Invoice
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Automatically create draft invoices based on a schedule
                          </p>
                        </div>
                        <Switch
                          id="drawer-auto-generate-toggle"
                          checked={drawerSettings.autoGenerateEnabled}
                          onCheckedChange={(checked) => handleDrawerSettingsChange("autoGenerateEnabled", checked)}
                          disabled={!drawerEditMode}
                        />
                      </div>

                      {drawerSettings.autoGenerateEnabled && (
                        <div className="ml-0 space-y-2 pt-2">
                          {/* Monthly / Quarterly Pattern Options */}
                          {(drawerSettings.billingFrequency === "monthly" ||
                            drawerSettings.billingFrequency === "quarterly") && (
                            <div className="space-y-1.5">
                              <Label className="text-sm font-medium">Generate invoice on:</Label>
                              <RadioGroup
                                value={drawerSettings.monthlyPattern}
                                onValueChange={(value) => handleDrawerSettingsChange("monthlyPattern", value)}
                                className="space-y-2"
                                disabled={!drawerEditMode}
                              >
                                <div className="space-y-2">
                                  <RadioGroupItem value="specific-day" id="drawer-specific-day">
                                    Specific day of month
                                  </RadioGroupItem>
                                  {drawerSettings.monthlyPattern === "specific-day" && (
                                    <div className="space-y-1 ml-7">
                                      <Input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={drawerSettings.monthlyDay}
                                        disabled={!drawerEditMode}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          if (value === "") {
                                            handleDrawerSettingsChange("monthlyDay", "");
                                            return;
                                          }
                                          const numValue = parseInt(value, 10);
                                          if (!isNaN(numValue) && numValue >= 1 && numValue <= 31) {
                                            handleDrawerSettingsChange("monthlyDay", value);
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const value = e.target.value;
                                          if (value === "" || parseInt(value, 10) < 1 || parseInt(value, 10) > 31) {
                                            handleDrawerSettingsChange("monthlyDay", "1");
                                          }
                                        }}
                                        className="w-32"
                                        placeholder="1-31"
                                        aria-invalid={
                                          drawerSettings.monthlyDay === "" ||
                                          parseInt(drawerSettings.monthlyDay, 10) < 1 ||
                                          parseInt(drawerSettings.monthlyDay, 10) > 31
                                        }
                                      />
                                      <p className="text-xs text-muted-foreground">Enter a day between 1 and 31</p>
                                    </div>
                                  )}
                                </div>

                                <RadioGroupItem value="last-day" id="drawer-last-day">
                                  Last day of the month
                                </RadioGroupItem>

                                <RadioGroupItem value="first-working-day" id="drawer-first-working-day">
                                  First working day of the month (Mon-Fri)
                                </RadioGroupItem>

                                <RadioGroupItem value="last-working-day" id="drawer-last-working-day">
                                  Last working day of the month (Mon-Fri)
                                </RadioGroupItem>
                              </RadioGroup>
                              {drawerSettings.billingFrequency === "quarterly" && (
                                <p className="text-xs text-muted-foreground">
                                  Draft invoices will be generated on this day, once every three months
                                </p>
                              )}
                            </div>
                          )}

                          {/* Weekly / Fortnightly Pattern Options */}
                          {(drawerSettings.billingFrequency === "weekly" ||
                            drawerSettings.billingFrequency === "fortnightly") && (
                            <div className="space-y-1.5">
                              <Label htmlFor="drawer-weekly-day" className="text-sm font-medium">
                                Generate invoice on:
                              </Label>
                              <Select
                                value={drawerSettings.weeklyDay}
                                onValueChange={(value) => handleDrawerSettingsChange("weeklyDay", value)}
                                disabled={!drawerEditMode}
                              >
                                <SelectTrigger id="drawer-weekly-day" className="w-full">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="monday">Monday</SelectItem>
                                  <SelectItem value="tuesday">Tuesday</SelectItem>
                                  <SelectItem value="wednesday">Wednesday</SelectItem>
                                  <SelectItem value="thursday">Thursday</SelectItem>
                                  <SelectItem value="friday">Friday</SelectItem>
                                  <SelectItem value="saturday">Saturday</SelectItem>
                                  <SelectItem value="sunday">Sunday</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground">
                                {drawerSettings.billingFrequency === "fortnightly"
                                  ? "Draft invoices will be generated on this day, every two weeks"
                                  : "Draft invoices will be generated every week on this day"}
                              </p>
                            </div>
                          )}

                          {/* Preview of next invoice date */}
                          {(() => {
                            const nextDate = getNextInvoiceDateForDrawer();
                            if (nextDate) {
                              return (
                                <div className="rounded-lg bg-muted/50 border border-muted p-4 text-sm">
                                  <div className="flex items-start gap-3">
                                    <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                                    <div>
                                      <span className="font-semibold text-foreground">Preview</span>
                                      <p className="text-muted-foreground mt-1">
                                        Next invoice will generate on{" "}
                                        <span className="font-medium text-foreground">
                                          {nextDate.toLocaleDateString("en-US", {
                                            weekday: "long",
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                          })}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Recharge Configuration Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Recharge Configuration</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Expense Recharge */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-expense-recharge" className="text-base font-semibold">Expense Recharge</Label>
                      <Select
                        value={drawerSettings.expenseRecharge}
                        onValueChange={(value) => {
                          handleDrawerSettingsChange("expenseRecharge", value);
                          if (value !== "markup") {
                            handleDrawerSettingsChange("expenseMarkupPercent", "");
                          }
                        }}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-expense-recharge">
                          <SelectValue placeholder="Select expense recharge method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="at-cost">At Cost</SelectItem>
                          <SelectItem value="markup">Mark up %</SelectItem>
                          <SelectItem value="not-rechargeable">Not Rechargeable</SelectItem>
                        </SelectContent>
                      </Select>

                      {drawerSettings.expenseRecharge === "markup" && (
                        <div className="ml-7 mt-1.5 space-y-1.5">
                          <Label htmlFor="drawer-expense-markup-percent" className="text-sm">
                            Markup Percentage
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="drawer-expense-markup-percent"
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder="0.00"
                              value={drawerSettings.expenseMarkupPercent}
                              onChange={(e) => handleDrawerSettingsChange("expenseMarkupPercent", e.target.value)}
                              className="w-32"
                              disabled={!drawerEditMode}
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Expenses will be billed at cost plus this percentage
                          </p>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Purchase Recharge */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-purchase-recharge" className="text-base font-semibold">Purchase Recharge</Label>
                      <Select
                        value={drawerSettings.purchaseRecharge}
                        onValueChange={(value) => {
                          handleDrawerSettingsChange("purchaseRecharge", value);
                          if (value !== "markup") {
                            handleDrawerSettingsChange("purchaseMarkupPercent", "");
                          }
                        }}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-purchase-recharge">
                          <SelectValue placeholder="Select purchase recharge method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="at-cost">At Cost</SelectItem>
                          <SelectItem value="markup">Mark up %</SelectItem>
                          <SelectItem value="not-rechargeable">Not Rechargeable</SelectItem>
                        </SelectContent>
                      </Select>

                      {drawerSettings.purchaseRecharge === "markup" && (
                        <div className="ml-7 mt-1.5 space-y-1.5">
                          <Label htmlFor="drawer-purchase-markup-percent" className="text-sm">
                            Markup Percentage
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="drawer-purchase-markup-percent"
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder="0.00"
                              value={drawerSettings.purchaseMarkupPercent}
                              onChange={(e) => handleDrawerSettingsChange("purchaseMarkupPercent", e.target.value)}
                              className="w-32"
                              disabled={!drawerEditMode}
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Purchases will be billed at cost plus this percentage
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Invoice Settings Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Banknote className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Invoice Settings</CardTitle>
                    </div>
                    <CardDescription>Configure invoice caps and billing limits for this project</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Not to Exceed Cap */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="drawer-not-to-exceed-cap" className="text-base font-semibold">
                            Invoice Cap
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Set a maximum amount that can be billed on each invoice
                          </p>
                        </div>
                        <Switch
                          id="drawer-not-to-exceed-cap"
                          checked={drawerSettings.notToExceedCapEnabled}
                          onCheckedChange={(checked) => {
                            handleDrawerSettingsChange("notToExceedCapEnabled", checked);
                            if (!checked) {
                              handleDrawerSettingsChange("notToExceedCapAmount", "");
                            }
                          }}
                          disabled={!drawerEditMode}
                        />
                      </div>

                      {drawerSettings.notToExceedCapEnabled && (
                        <div className="ml-0 mt-1.5 space-y-1.5">
                          <Label htmlFor="drawer-cap-amount" className="text-sm">
                            Maximum Amount Per Invoice
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="drawer-cap-amount"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={drawerSettings.notToExceedCapAmount}
                              onChange={(e) => handleDrawerSettingsChange("notToExceedCapAmount", e.target.value)}
                              className="w-40"
                              disabled={!drawerEditMode}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Any invoice exceeding this amount will require manual review
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : project?.type === "fixed-price" ? (
              <>
                {/* Fixed Fee — Billing Configuration Card */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-muted-foreground" />
                      <CardTitle>Billing Configuration</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Billing Trigger Type */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-billing-trigger-type" className="text-base font-semibold">Billing Trigger Type</Label>
                      <Select
                        value={drawerSettings.billingTriggerType || project.billingTriggerType || ''}
                        onValueChange={(value) => handleDrawerSettingsChange("billingTriggerType", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-billing-trigger-type" className="w-full">
                          <SelectValue placeholder="Select trigger type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date">Date-based</SelectItem>
                          <SelectItem value="percent-complete">% Complete</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Determines when billing milestones are evaluated.
                      </p>
                    </div>

                    {/* Task 13.1 — Completion % Calculation — only for percent-complete projects */}
                    {(drawerSettings.billingTriggerType || project.billingTriggerType) === 'percent-complete' && (
                      <>
                        <Separator />
                        <div className="space-y-2">
                          <Label className="text-base font-semibold">Completion % Calculation</Label>
                          <p className="text-sm text-muted-foreground">
                            Controls how the project's completion percentage is determined for milestone evaluation.
                          </p>
                          <Select
                            value={project.completionPercentMode ?? 'based-on-time'}
                            onValueChange={(value) => {
                              if (drawerEditMode) {
                                handleCompletionModeChange(value as 'manual' | 'based-on-time');
                              }
                            }}
                            disabled={!drawerEditMode}
                          >
                            <SelectTrigger id="drawer-completion-mode" className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="based-on-time">Based on Approved Time</SelectItem>
                              <SelectItem value="manual">Manual</SelectItem>
                            </SelectContent>
                          </Select>
                          {project.completionPercentMode === 'based-on-time' ? (
                            <p className="text-xs text-muted-foreground">
                              Completion % is automatically calculated from approved hours ÷ budget hours. Any existing override will be cleared when switching to this mode.
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Completion % is frozen at the current value. The PM must update it manually via the Project Overview.
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    <Separator />

                    {/* Invoice Template */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-fixed-invoice-template" className="text-base font-semibold">
                        Invoice Template
                      </Label>
                      <Select
                        value={drawerSettings.invoiceTemplateId ? drawerSettings.invoiceTemplateId.toString() : (invoiceTemplates.find(t => t.isDefault)?.id || invoiceTemplates[0]?.id || '').toString()}
                        onValueChange={(value) => handleDrawerSettingsChange("invoiceTemplateId", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-fixed-invoice-template" className="w-full">
                          <SelectValue placeholder="Select invoice template" />
                        </SelectTrigger>
                        <SelectContent>
                          {invoiceTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name}{t.isDefault ? ' (Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Choose the template to use when generating invoices for this project
                      </p>
                    </div>

                    <Separator />

                    {/* Invoice Email Template */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-fixed-invoice-email-template" className="text-base font-semibold">
                        Invoice Email Template
                      </Label>
                      <Select
                        value={drawerSettings.invoiceEmailTemplateId ? drawerSettings.invoiceEmailTemplateId.toString() : (invoiceEmailTemplates.find(t => t.isDefault)?.id || invoiceEmailTemplates[0]?.id || '').toString()}
                        onValueChange={(value) => handleDrawerSettingsChange("invoiceEmailTemplateId", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-fixed-invoice-email-template" className="w-full">
                          <SelectValue placeholder="Select invoice email template" />
                        </SelectTrigger>
                        <SelectContent>
                          {invoiceEmailTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name}{t.isDefault ? ' (Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Default email template used when sending invoices for this project
                      </p>
                    </div>

                    <Separator />

                    {/* Credit Note Email Template */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-fixed-credit-note-email-template" className="text-base font-semibold">
                        Credit Note Email Template
                      </Label>
                      <Select
                        value={drawerSettings.creditNoteEmailTemplateId ? drawerSettings.creditNoteEmailTemplateId.toString() : (creditNoteEmailTemplates.find(t => t.isDefault)?.id || creditNoteEmailTemplates[0]?.id || '').toString()}
                        onValueChange={(value) => handleDrawerSettingsChange("creditNoteEmailTemplateId", value)}
                        disabled={!drawerEditMode}
                      >
                        <SelectTrigger id="drawer-fixed-credit-note-email-template" className="w-full">
                          <SelectValue placeholder="Select credit note email template" />
                        </SelectTrigger>
                        <SelectContent>
                          {creditNoteEmailTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id.toString()}>
                              {t.name}{t.isDefault ? ' (Default)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Default email template used when sending credit notes for this project
                      </p>
                    </div>

                    <Separator />

                    {/* Default Cost Centre — required for finance system posting */}
                    <div className="space-y-1.5">
                      <Label htmlFor="drawer-fixed-cost-centre" className="text-base font-semibold">
                        Default Cost Centre
                      </Label>
                      {costCentres.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          No cost centres are linked to this project yet. Add them in the Cost Centres tab before configuring billing.
                        </p>
                      ) : (
                        <>
                          <Select
                            value={drawerSettings.defaultCostCentreCode ?? ""}
                            onValueChange={(value) => {
                              handleDrawerSettingsChange("defaultCostCentreCode", value);
                              setDefaultCostCentreError(false);
                            }}
                            disabled={!drawerEditMode}
                          >
                            <SelectTrigger
                              id="drawer-fixed-cost-centre"
                              className={cn("w-full", defaultCostCentreError && "border-destructive")}
                            >
                              <SelectValue placeholder="Select a cost centre…" />
                            </SelectTrigger>
                            <SelectContent>
                              {costCentres.map((cc) => (
                                <SelectItem key={cc.id} value={cc.code}>
                                  {cc.code} – {cc.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {defaultCostCentreError ? (
                            <p className="text-xs text-destructive">
                              A default cost centre is required for finance system posting.
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Required for finance system posting. Applied to all invoices generated from this project.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  Billing settings are not available for this project type.
                </p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Invoice Generation Wizard */}
      <InvoiceGenerationWizard
        open={invoiceWizardOpen}
        onOpenChange={(open) => {
          setInvoiceWizardOpen(open);
          // Reset Fixed Fee milestone and Edit-Draft state when wizard closes so
          // they don't carry over to the next open.
          if (!open) {
            setFixedFeeMilestone(null);
            setEditDraftData(null);
          }
        }}
        editDraft={editDraftData}
        onReturnDraftToWip={dataMode === 'live' ? async (invoiceId: number) => {
          await handleDeleteDraft(invoiceId);
          setEditDraftData(null);
          setBillingSubTab('unbilled');
        } : undefined}
        selectedWipItems={wipItems.filter((item) => selectedWipItems.includes(item.id)) as { id: string; date: string; resource: string; description: string; type: 'Time' | 'Expense' | 'Purchase'; quantity: number; unit: string; rate: number; amount: number; costCentre: string; reference: string; costRate: number; }[]}
        projectName={project?.name || ""}
        projectCode={project?.code || ""}
        client={client}
        company={currentCompany}
        expenseRecharge={projectSettings.expenseRecharge}
        expenseMarkupPercent={projectSettings.expenseMarkupPercent}
        purchaseRecharge={projectSettings.purchaseRecharge}
        purchaseMarkupPercent={projectSettings.purchaseMarkupPercent}
        notToExceedCapEnabled={projectSettings.notToExceedCapEnabled}
        notToExceedCapAmount={projectSettings.notToExceedCapAmount}
        invoiceTemplateId={projectSettings.invoiceTemplateId != null ? projectSettings.invoiceTemplateId : undefined}
        defaultEmailTemplateId={projectSettings.invoiceEmailTemplateId}
        projectDescription={project?.description || ""}
        projectManagerName={projectManager?.name || ""}
        billingEvent={fixedFeeMilestone != null ? { name: fixedFeeMilestone.name, amount: fixedFeeMilestone.amount, dueDate: fixedFeeMilestone.dueDate } : undefined}
        onTemplateChange={async (templateId) => {
          // Task 2.12c — persist the template change back to the project's billing config
          setProjectSettings((prev) => ({ ...prev, invoiceTemplateId: templateId.toString() }));
          if (numericProjectId) {
            await updateProjectBillingConfig(numericProjectId, { invoiceTemplateId: templateId });
          }
        }}
        onCreateSalesTransaction={dataMode === 'live' ? async (invoice: any): Promise<{ success: boolean; pdfBase64?: string; invoiceNumber?: string }> => {
          try {
            setBillingActionLoading(true);

            const invoiceDateStr = invoice.date ? toLocalDateString(new Date(invoice.date)) : undefined;
            const dueDateStr = invoice.dueDate ? toLocalDateString(new Date(invoice.dueDate)) : undefined;

            let createdInvoice: Awaited<ReturnType<typeof createBillingDraft>>;

            if (invoice.editingDraftId) {
              // Edit-Draft mode: update the existing draft (edits/removals) then post it.
              await applyDraftLineChanges(invoice.editingDraftId, editDraftData?.lineItems ?? [], invoice.draftLineState ?? []);
              const { invoice: existing } = await fetchBillingInvoiceById(invoice.editingDraftId);
              createdInvoice = existing;
            } else if (invoice.isFixedFee && invoice.billingEvent) {
              const description = invoice.fixedFeeDescription || invoice.billingEvent.name;
              createdInvoice = await createBillingDraft(
                numericProjectId,
                [],
                description,
                undefined,
                invoiceDateStr,
                dueDateStr,
                {
                  isFixedFee: true,
                  fixedFeeAmount: invoice.billingEvent.amount,
                  costCentreCode: projectSettings.defaultCostCentreCode ?? undefined,
                  milestoneId: fixedFeeMilestone?.id,
                },
              );
            } else {
              const wipItemIds = invoice.lineItems
                .map((li: { originalWipId?: string }) => {
                  if (!li.originalWipId) return null;
                  const numId = parseInt(li.originalWipId.replace("wip-", ""), 10);
                  return isNaN(numId) ? null : numId;
                })
                .filter((id: number | null): id is number => id !== null);

              if (wipItemIds.length === 0) {
                toast.error('No valid WIP items found');
                return { success: false };
              }

              const description = invoice.reference || undefined;
              const notes = invoice.notes || undefined;
              createdInvoice = await createBillingDraft(numericProjectId, wipItemIds, description, notes, invoiceDateStr, dueDateStr, {
                hasAgreedValue: !!invoice.agreedValueWriteOff,
                agreedValueWriteOffReason: invoice.agreedValueWriteOff?.writeOffReason,
              });

              const edits: Array<{ wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string }> = [];
              for (const li of invoice.lineItems) {
                if (!li.originalWipId) continue;
                const numId = parseInt(li.originalWipId.replace("wip-", ""), 10);
                if (isNaN(numId)) continue;
                const originalWip = wipItems.find((w) => w.id === li.originalWipId);
                if (!originalWip) continue;
                const edit: { wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string } = { wipLedgerId: numId };
                let hasChanges = false;
                if (li.quantity !== originalWip.quantity) { edit.quantity = li.quantity; hasChanges = true; }
                if (li.rate !== originalWip.rate) { edit.rate = li.rate; hasChanges = true; }
                if (li.amount !== originalWip.amount) { edit.netAmount = li.amount; hasChanges = true; }
                if (hasChanges) edits.push(edit);
              }
              if (edits.length > 0) {
                await updateBillingDraft(createdInvoice.id, { edits });
              }
            }

            // Post the draft to Access Financials to create the sales transaction
            const result = await postBillingDraftToFinancials(createdInvoice.id);

            if (result.success) {
              toast.success("Invoice created and posted to Access Financials", {
                description: result.financialsIds?.length
                  ? `Transaction ID${result.financialsIds.length > 1 ? "s" : ""}: ${result.financialsIds.join(", ")}`
                  : undefined,
              });

              // Generate PDF and store against the invoice, then return base64 for email
              let pdfBase64: string | undefined;
              try {
                const { lineItems: createdLineItems } = await fetchBillingInvoiceById(createdInvoice.id);
                const pdfTransaction: InvoicePdfTransaction = {
                  invoiceNumber: createdInvoice.invoiceNumber ?? `#${createdInvoice.id}`,
                  date: invoice.date ? toLocalDateString(new Date(invoice.date)) : toLocalDateString(new Date()),
                  dueDate: invoice.dueDate ? toLocalDateString(new Date(invoice.dueDate)) : undefined,
                  status: "Sent",
                  description: invoice.fixedFeeDescription || invoice.billingEvent?.name || "",
                  amount: invoice.totals?.gross ?? invoice.billingEvent?.amount ?? 0,
                  projectName: project?.name,
                  accountName: project?.clientNames,
                  projectDescription: project?.description || '',
                  projectManager: projectManager?.name || '',
                };
                const companyDetails = await fetchFullCompanyDetails(currentCompany);
                const pdfResult = await generateAndStoreInvoicePdf(pdfTransaction, createdInvoice.id, createdLineItems, companyDetails, undefined, undefined, projectSettings.invoiceTemplateId, invoice.invoiceNotes || undefined);
                pdfBase64 = await blobToBase64(pdfResult.pdfBlob);

                // Upload PDF to ADM using user's token (browser-side)
                try {
                  const admCtx = await getDocumentContext();
                  if (admCtx) {
                    const admDocId = await uploadInvoicePdfToAdm(admCtx, getAdmAccessToken, pdfResult.pdfBlob, {
                      invoiceNumber: createdInvoice.invoiceNumber ?? `#${createdInvoice.id}`,
                      documentDate: invoice.date ? toLocalDateString(new Date(invoice.date)) : toLocalDateString(new Date()),
                      description: invoice.fixedFeeDescription || invoice.billingEvent?.name || "",
                      accountName: project?.clientNames,
                      transactionType: "Invoice",
                    });
                    await storeAdmDocumentId(createdInvoice.id, admDocId);
                  }
                } catch (admErr) {
                  console.warn("ADM upload failed (non-blocking):", admErr);
                  toast.warning("Document upload to ADM failed", {
                    description: admErr instanceof Error ? admErr.message : "The invoice was created but the document was not uploaded to Access Document Management.",
                  });
                }
              } catch (pdfErr) {
                console.error("PDF generation failed (non-blocking):", pdfErr);
              }

              return { success: true, pdfBase64, invoiceNumber: createdInvoice.invoiceNumber ?? `INV-${createdInvoice.id}` };
            } else if (result.validationErrors && result.validationErrors.length > 0) {
              const errorMessages = result.validationErrors
                .map((e) => {
                  const details = e.details?.map((d) => `${d.field}: ${d.value}`).join(", ");
                  return details ? `${e.message} (${details})` : e.message;
                })
                .join("\n");
              toast.error("Financials validation failed", { description: errorMessages, duration: 10000 });
              return { success: false };
            } else {
              toast.error("Failed to post invoice to Financials", {
                description: result.error || "An unexpected error occurred.",
                duration: 8000,
              });
              return { success: false };
            }
          } catch (error: any) {
            console.error("Failed to create sales transaction:", error);
            const errorMsg = error?.message || error?.metadata?.apiError?.error || "";
            if (errorMsg.includes("are available for billing") || errorMsg.includes("already been invoiced")) {
              toast.error("Invoice could not be created", {
                description: "The selected items have already been invoiced by another user or process. Please refresh the page and try again.",
                duration: 8000,
              });
            } else {
              handleApiError(error, `/api/billing/${numericProjectId}/drafts`, "POST");
            }
            return { success: false };
          } finally {
            setBillingActionLoading(false);
          }
        } : undefined}
        onSaveAsDraft={dataMode === 'live' ? async (invoice: any) => {
          try {
            setBillingActionLoading(true);

            const invoiceDateStr = invoice.date ? toLocalDateString(new Date(invoice.date)) : undefined;
            const dueDateStr = invoice.dueDate ? toLocalDateString(new Date(invoice.dueDate)) : undefined;

            let createdInvoiceId: number | undefined;

            if (invoice.editingDraftId) {
              // Edit-Draft mode: update the existing draft (edits/removals) in place.
              await applyDraftLineChanges(invoice.editingDraftId, editDraftData?.lineItems ?? [], invoice.draftLineState ?? []);
              createdInvoiceId = invoice.editingDraftId;
            } else if (invoice.isFixedFee && invoice.billingEvent) {
              const description = invoice.fixedFeeDescription || invoice.billingEvent.name;
              const ffInvoice = await createBillingDraft(
                numericProjectId,
                [],
                description,
                undefined,
                invoiceDateStr,
                dueDateStr,
                {
                  isFixedFee: true,
                  fixedFeeAmount: invoice.billingEvent.amount,
                  costCentreCode: projectSettings.defaultCostCentreCode ?? undefined,
                  milestoneId: fixedFeeMilestone?.id,
                },
              );
              createdInvoiceId = ffInvoice.id;
            } else {
              const wipItemIds = invoice.lineItems
                .map((li: { originalWipId?: string }) => {
                  if (!li.originalWipId) return null;
                  const numId = parseInt(li.originalWipId.replace("wip-", ""), 10);
                  return isNaN(numId) ? null : numId;
                })
                .filter((id: number | null): id is number => id !== null);

              if (wipItemIds.length === 0) {
                toast.error('No valid WIP items found');
                return;
              }

              const description = invoice.reference || undefined;
              const notes = invoice.notes || undefined;
              const createdInvoice = await createBillingDraft(numericProjectId, wipItemIds, description, notes, invoiceDateStr, dueDateStr, {
                hasAgreedValue: !!invoice.agreedValueWriteOff,
                agreedValueWriteOffReason: invoice.agreedValueWriteOff?.writeOffReason,
              });
              createdInvoiceId = createdInvoice.id;

              const edits: Array<{ wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string }> = [];
              for (const li of invoice.lineItems) {
                if (!li.originalWipId) continue;
                const numId = parseInt(li.originalWipId.replace("wip-", ""), 10);
                if (isNaN(numId)) continue;
                const originalWip = wipItems.find((w) => w.id === li.originalWipId);
                if (!originalWip) continue;
                const edit: { wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string } = { wipLedgerId: numId };
                let hasChanges = false;
                if (li.quantity !== originalWip.quantity) { edit.quantity = li.quantity; hasChanges = true; }
                if (li.rate !== originalWip.rate) { edit.rate = li.rate; hasChanges = true; }
                if (li.amount !== originalWip.amount) { edit.netAmount = li.amount; hasChanges = true; }
                if (hasChanges) edits.push(edit);
              }
              if (edits.length > 0) {
                await updateBillingDraft(createdInvoice.id, { edits });
              }
            }

            // Persist email draft data (Template, To, CC, BCC, Subject, Body) if provided
            if (createdInvoiceId && invoice.emailDraft) {
              const { to, cc, bcc, subject, body, templateId } = invoice.emailDraft;
              await updateDraftEmailData(createdInvoiceId, {
                to: to?.length ? to.join(', ') : undefined,
                cc: cc?.length ? cc.join(', ') : undefined,
                bcc: bcc?.length ? bcc.join(', ') : undefined,
                subject: subject || undefined,
                body: body || undefined,
                templateId: templateId || undefined,
              });
            }

            toast.success('Invoice saved as draft');
            setSelectedWipItems([]);
            await refetchBillingData();
          } catch (error) {
            console.error("Failed to save draft:", error);
            handleApiError(error, `/api/billing/${numericProjectId}/drafts`, "POST");
            throw error;
          } finally {
            setBillingActionLoading(false);
          }
        } : undefined}
        onSendComplete={() => {
          setSelectedWipItems([]);
          refetchBillingData();
          setBillingSubTab('unbilled');
        }}
        onInvoiceGenerated={async (invoice) => {
          if (dataMode !== "live") {
            console.log("Invoice generated (demo mode):", invoice);

            // Feature 9 — Invoice Numbering (demo mode):
            // When auto-numbering is ON: assign the next sequential number automatically.
            // When auto-numbering is OFF: use the manually entered number from the wizard.
            //
            // TODO [ENGINEERING]: Remove this entire demo block once the real API is wired up.
            //   In live mode the invoice number is assigned by Access Financials on posting
            //   and returned in the API response (handled below in the live-mode try/catch).
            const existingNumbers = billingInvoices
              .map((inv) => inv.invoiceNumber)
              .filter(Boolean) as string[];

            let newInvoiceNumber: string;
            if (invoice.invoiceNumber) {
              // Manual mode — user supplied the number
              newInvoiceNumber = invoice.invoiceNumber;
            } else {
              // Auto mode — derive next sequential number from existing mock data
              const year = new Date().getFullYear();
              const thisYearNums = existingNumbers
                .filter((n) => n.startsWith(`INV-${year}-`))
                .map((n) => parseInt(n.replace(`INV-${year}-`, ''), 10))
                .filter((n) => !isNaN(n));
              const nextSeq = thisYearNums.length > 0 ? Math.max(...thisYearNums) + 1 : 1;
              newInvoiceNumber = `INV-${year}-${String(nextSeq).padStart(3, '0')}`;
            }

            // Build a new BillingInvoice row and prepend it to the local list.
            // All fields conform to the BillingInvoice interface in data-service.ts.
            // status: 1 = Sent (see INVOICE_STATUS_MAP in data-service.ts)
            const invoiceTotal = invoice.totals?.gross ?? invoice.billingEvent?.amount ?? 0;
            const todayIso = new Date().toISOString();
            const dueDateIso = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
            const subtotalAmt = invoiceTotal / 1.2;
            const taxAmt = invoiceTotal - subtotalAmt;
            const newInvoiceRow = {
              id: Date.now(),
              projectId: numericProjectId ?? 0,
              invoiceNumber: newInvoiceNumber,
              invoiceDate: todayIso,
              dueDate: dueDateIso,
              description: invoice.fixedFeeDescription || invoice.billingEvent?.name || 'Invoice',
              subtotal: subtotalAmt,
              taxRate: 20,
              taxAmount: taxAmt,
              grossTotal: invoiceTotal,
              status: 1, // Sent
            };
            setBillingInvoices((prev) => [newInvoiceRow, ...prev]);

            toast.success(`Invoice ${newInvoiceNumber} generated`, {
              description: invoice.invoiceNumber
                ? 'Manual invoice number applied.'
                : 'Invoice number assigned automatically.',
            });
            setSelectedWipItems([]);
            return;
          }

          try {
            setBillingActionLoading(true);

            const invoiceDateStr = invoice.date ? toLocalDateString(new Date(invoice.date)) : undefined;
            const dueDateStr = invoice.dueDate ? toLocalDateString(new Date(invoice.dueDate)) : undefined;

            let createdInvoice: Awaited<ReturnType<typeof createBillingDraft>>;

            if (invoice.editingDraftId) {
              // ── Edit-Draft path: update the existing draft then post it ──
              await applyDraftLineChanges(invoice.editingDraftId, editDraftData?.lineItems ?? [], invoice.draftLineState ?? []);
              const { invoice: existing } = await fetchBillingInvoiceById(invoice.editingDraftId);
              createdInvoice = existing;
            } else if (invoice.isFixedFee && invoice.billingEvent) {
              // ── Fixed Fee path: create a draft with the fixed-fee amount directly ──
              const description = invoice.fixedFeeDescription || invoice.billingEvent.name;
              createdInvoice = await createBillingDraft(
                numericProjectId,
                [],           // no WIP items for fixed fee
                description,
                undefined,    // notes
                invoiceDateStr,
                dueDateStr,
                {
                  isFixedFee: true,
                  fixedFeeAmount: invoice.billingEvent.amount,
                  costCentreCode: projectSettings.defaultCostCentreCode ?? undefined,
                  milestoneId: fixedFeeMilestone?.id,
                },
              );
            } else {
              // ── WIP-based path (existing behaviour) ──
              const wipItemIds = invoice.lineItems
                .map((li: { originalWipId?: string }) => {
                  if (!li.originalWipId) return null;
                  const numId = parseInt(li.originalWipId.replace("wip-", ""), 10);
                  return isNaN(numId) ? null : numId;
                })
                .filter((id: number | null): id is number => id !== null);

              if (wipItemIds.length === 0) {
                console.warn("No valid WIP item IDs found in wizard line items");
                return;
              }

              const description = invoice.reference || undefined;
              const notes = invoice.notes || undefined;
              createdInvoice = await createBillingDraft(numericProjectId, wipItemIds, description, notes, invoiceDateStr, dueDateStr, {
                hasAgreedValue: !!invoice.agreedValueWriteOff,
                agreedValueWriteOffReason: invoice.agreedValueWriteOff?.writeOffReason,
              });

              // Apply any edits the user made in the wizard (quantity/rate/description changes)
              const edits: Array<{ wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string }> = [];
              for (const li of invoice.lineItems) {
                if (!li.originalWipId) continue;
                const numId = parseInt(li.originalWipId.replace("wip-", ""), 10);
                if (isNaN(numId)) continue;

                const originalWip = wipItems.find((w) => w.id === li.originalWipId);
                if (!originalWip) continue;

                const edit: { wipLedgerId: number; quantity?: number; rate?: number; netAmount?: number; description?: string } = { wipLedgerId: numId };
                let hasChanges = false;

                if (li.quantity !== originalWip.quantity) {
                  edit.quantity = li.quantity;
                  hasChanges = true;
                }
                if (li.rate !== originalWip.rate) {
                  edit.rate = li.rate;
                  hasChanges = true;
                }
                if (li.amount !== originalWip.amount) {
                  edit.netAmount = li.amount;
                  hasChanges = true;
                }

                if (hasChanges) {
                  edits.push(edit);
                }
              }

              if (edits.length > 0) {
                await updateBillingDraft(createdInvoice.id, { edits });
              }
            }

            // Post the draft to Access Financials to create the sales transaction
            const result = await postBillingDraftToFinancials(createdInvoice.id);

            if (result.success) {
              toast.success("Invoice generated and posted to Access Financials", {
                description: result.financialsIds?.length
                  ? `Transaction ID${result.financialsIds.length > 1 ? "s" : ""}: ${result.financialsIds.join(", ")}`
                  : undefined,
              });

              // Auto-generate PDF and store against the invoice
              try {
                const { lineItems: createdLineItems } = await fetchBillingInvoiceById(createdInvoice.id);
                const pdfTransaction: InvoicePdfTransaction = {
                  invoiceNumber: createdInvoice.invoiceNumber ?? `#${createdInvoice.id}`,
                  date: invoice.date ? toLocalDateString(new Date(invoice.date)) : toLocalDateString(new Date()),
                  dueDate: invoice.dueDate ? toLocalDateString(new Date(invoice.dueDate)) : undefined,
                  status: "Sent",
                  description: invoice.fixedFeeDescription || invoice.billingEvent?.name || "",
                  amount: invoice.totals?.gross ?? invoice.billingEvent?.amount ?? 0,
                  projectName: project?.name,
                  accountName: project?.clientNames,
                  projectDescription: project?.description || '',
                  projectManager: projectManager?.name || '',
                };
                const companyDetails = await fetchFullCompanyDetails(currentCompany);
                const pdfResult = await generateAndStoreInvoicePdf(pdfTransaction, createdInvoice.id, createdLineItems, companyDetails, undefined, undefined, projectSettings.invoiceTemplateId, invoice.invoiceNotes || undefined);

                // Upload PDF to ADM using user's token (browser-side)
                try {
                  const admCtx = await getDocumentContext();
                  if (admCtx) {
                    const admDocId = await uploadInvoicePdfToAdm(admCtx, getAdmAccessToken, pdfResult.pdfBlob, {
                      invoiceNumber: createdInvoice.invoiceNumber ?? `#${createdInvoice.id}`,
                      documentDate: invoice.date ? toLocalDateString(new Date(invoice.date)) : toLocalDateString(new Date()),
                      description: invoice.fixedFeeDescription || invoice.billingEvent?.name || "",
                      accountName: project?.clientNames,
                      transactionType: "Invoice",
                    });
                    await storeAdmDocumentId(createdInvoice.id, admDocId);
                  }
                } catch (admErr) {
                  console.warn("ADM upload failed (non-blocking):", admErr);
                  toast.warning("Document upload to ADM failed", {
                    description: admErr instanceof Error ? admErr.message : "The invoice was created but the document was not uploaded to Access Document Management.",
                  });
                }
              } catch (pdfErr) {
                console.error("Auto PDF generation failed (non-blocking):", pdfErr);
              }

              setSelectedWipItems([]);
              await refetchBillingData();
            } else if (result.validationErrors && result.validationErrors.length > 0) {
              // Validation errors from Financials — invoice stays as draft
              const errorMessages = result.validationErrors
                .map((e) => {
                  const details = e.details?.map((d) => `${d.field}: ${d.value}`).join(", ");
                  return details ? `${e.message} (${details})` : e.message;
                })
                .join("\n");
              toast.error("Financials validation failed", {
                description: errorMessages,
                duration: 10000,
              });
              // Still refetch to show the draft invoice that was created
              setSelectedWipItems([]);
              await refetchBillingData();
            } else {
              // Unexpected error — invoice stays as draft
              toast.error("Failed to post invoice to Financials", {
                description: result.error || "An unexpected error occurred. The invoice has been saved as a draft.",
                duration: 8000,
              });
              setSelectedWipItems([]);
              await refetchBillingData();
            }
          } catch (error: any) {
            console.error("Failed to generate invoice:", error);
            const errorMsg = error?.message || error?.metadata?.apiError?.error || "";
            if (errorMsg.includes("are available for billing") || errorMsg.includes("already been invoiced")) {
              toast.error("Invoice could not be created", {
                description: "The selected items have already been invoiced by another user or process. Please refresh the page and try again.",
                duration: 8000,
              });
            } else {
              handleApiError(error, `/api/billing/${numericProjectId}/drafts`, "POST");
            }
          } finally {
            setBillingActionLoading(false);
          }
        }}
      />

      {/* Create Draft Dialog */}
      <CreateDraftDialog
        open={createDraftDialogOpen}
        onOpenChange={setCreateDraftDialogOpen}
        selectedItemCount={selectedWipItems.length}
        onConfirm={handleCreateDraft}
        loading={billingActionLoading}
      />

      {/* Invoice Details Sheet */}
      <InvoiceDetailsSheet
        invoiceId={selectedInvoiceId}
        open={invoiceDetailsSheetOpen}
        onOpenChange={setInvoiceDetailsSheetOpen}
        onDeleteDraft={handleDeleteDraft}
        onReturnToDraft={() => refetchBillingData()}
      />

      {/* Invoice Column Configurator */}
      <ColumnConfiguratorDrawer
        open={invoiceColumnConfigOpen}
        onOpenChange={setInvoiceColumnConfigOpen}
        columns={invoiceColumns}
        onSave={(updatedColumns) => {
          updateInvoiceColumns(updatedColumns);
        }}
        defaultColumns={DEFAULT_INVOICE_COLUMNS}
      />

      {/* WIP Column Configurator */}
      <ColumnConfiguratorDrawer
        open={wipColumnConfigOpen}
        onOpenChange={setWipColumnConfigOpen}
        columns={wipColumns}
        onSave={(updatedColumns) => {
          updateWipColumns(updatedColumns);
        }}
        defaultColumns={DEFAULT_WIP_COLUMNS}
      />

      {/* Client Side Panel */}
      <ClientSidePanel
        client={activePanelClientId !== null ? clients.find((c) => c.id === activePanelClientId) ?? null : null}
        open={activePanelClientId !== null}
        onClose={closeClientPanel}
        companyId={currentCompany?.id}
      />

      {/* Client Change Confirmation Dialog */}
      <ClientChangeConfirmDialog
        open={clientChangeDialogOpen}
        previousClient={previousClientForDialog}
        newClient={pendingClientChange?.selectedClient ?? null}
        actualHours={project?.actualHours ?? 0}
        onConfirm={handleClientChangeConfirm}
        onCancel={handleClientChangeCancel}
      />
    </div>
    </ProjectDetailsContext.Provider>
  );

}
